import {
  ArchiveIcon as HeroArchiveIcon,
  ArrowSmLeftIcon,
  ArrowSmRightIcon,
  BookOpenIcon as HeroBookIcon,
  CheckCircleIcon as HeroCheckCircleIcon,
  CloudUploadIcon as HeroCloudUploadIcon,
  PencilAltIcon,
  PlusCircleIcon as HeroPlusCircleIcon
} from '@heroicons/react/outline'
import {
  CheckIcon as HeroCheckIcon,
  ChevronRightIcon as HeroChevronRightIcon,
  TrashIcon as HeroTrashIcon
} from '@heroicons/react/solid'
import type React from 'react'
import { createVariants } from 'shared-ui/lib'

const defaultDisplay = 'inline-block'
const defaultIconClass = `h-5 ${defaultDisplay} flex-none ` as const

/**
 *  @group Icons
 */
export const ArrowLeftIcon = () => {
  return <ArrowSmLeftIcon className={defaultIconClass} />
}

/**
 * @group Icons
 */
export const UndoIcon = ({ color = 'currentColor', size = 'size-4 ', lineWidth = 1 }) => {
  return (
    <svg
      viewBox="0 0 16 16"
      className={size}
      strokeWidth={lineWidth}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2.97345 0.666666C2.97345 0.298477 3.29041 0 3.68141 0H9.27433C12.9818 0 16 2.75488 16 6.26666C16 9.76818 12.9928 12.6 9.27433 12.6L2.41714 12.6L4.81919 14.8619C5.09566 15.1223 5.09566 15.5444 4.81919 15.8047C4.54271 16.0651 4.09445 16.0651 3.81797 15.8047L0.207358 12.4047C0.0737299 12.2789 -0.000926841 12.108 8.6875e-06 11.93C0.000944174 11.7521 0.0773943 11.5819 0.212339 11.4573L3.82296 8.12396C4.10216 7.8662 4.55039 7.87038 4.82412 8.13329C5.09784 8.39621 5.09341 8.8183 4.81421 9.07606L2.44137 11.2667L9.27433 11.2667C12.2108 11.2667 14.5841 9.0318 14.5841 6.26666C14.5841 3.51178 12.2217 1.33333 9.27433 1.33333H3.68141C3.29041 1.33333 2.97345 1.03486 2.97345 0.666666Z"
        fill={color}
      />
    </svg>
  )
}

/**
 *  @group Icons
 */
export const ArchiveIcon = ({ color = '', size = 'h-4', lineWidth = 1 }) => {
  return <HeroArchiveIcon className={`${size} ${color}`} strokeWidth={lineWidth} />
}

/**
 *  @group Icons
 */
export const ArrowRightIcon = () => {
  return <ArrowSmRightIcon className={defaultIconClass} />
}

/**
 *  @group Icons
 */
export const CheckIcon: React.FC = () => {
  return <HeroCheckIcon className={`${defaultIconClass}text-green-400`} />
}

/**
 *  @group Icons
 */
export const CheckCircleIcon = ({ color = '', size = 'h-4', lineWidth = 1 }) => {
  return <HeroCheckCircleIcon className={`${size} ${color}`} strokeWidth={lineWidth} />
}

/**
 *  @group Icons
 */
export const ChevronDownIcon = ({
  fillClassName = 'text-green-500',
  className = '',
  disabled = false
} = {}) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="6"
      viewBox="0 0 12 6"
      fill="none"
      className={`${disabled ? 'text-gray-400' : fillClassName} ${className}`}
    >
      <path fillRule="evenodd" clipRule="evenodd" d="M12 0L6 6L0 0H12Z" fill="currentColor" />
    </svg>
  )
}

export const SortTableIcon = ({ active = false, asc = false }) => (
  <div className="relative">
    <ChevronDownIcon
      fillClassName={active && asc ? 'text-black' : 'text-gray-300'}
      className="mb-1 h-[5px] rotate-180"
    />
    <ChevronDownIcon
      fillClassName={active && !asc ? 'text-black' : 'text-gray-300'}
      className="h-[5px]"
    />
  </div>
)

/**
 *  @group Icons
 */
export const ChevronRightIcon = () => {
  return <HeroChevronRightIcon className={`${defaultDisplay} h-4`} />
}

/**
 *  @group Icons
 */
export const FilterIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className="text-black"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.82928 2H15C15.5523 2 16 2.44772 16 3C16 3.55228 15.5523 4 15 4H7.82928C7.41745 5.16519 6.30621 6 5 6C3.69379 6 2.58255 5.16519 2.17072 4H1C0.447723 4 0 3.55228 0 3C0 2.44772 0.447723 2 1 2H2.17072C2.58255 0.834808 3.69379 0 5 0C6.30621 0 7.41745 0.834808 7.82928 2ZM5 4C5.55228 4 6 3.55228 6 3C6 2.44772 5.55228 2 5 2C4.44772 2 4 2.44772 4 3C4 3.55228 4.44772 4 5 4Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1 7C0.447723 7 0 7.44772 0 8C0 8.55228 0.447723 9 1 9H8.17072C8.58255 10.1652 9.69379 11 11 11C12.3062 11 13.4174 10.1652 13.8293 9H15C15.5523 9 16 8.55228 16 8C16 7.44772 15.5523 7 15 7H13.8293C13.4174 5.83481 12.3062 5 11 5C9.69379 5 8.58255 5.83481 8.17072 7H1ZM11 9C11.5523 9 12 8.55228 12 8C12 7.44772 11.5523 7 11 7C10.4477 7 10 7.44772 10 8C10 8.55228 10.4477 9 11 9Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1 12C0.447723 12 0 12.4477 0 13C0 13.5523 0.447723 14 1 14H2.17072C2.58255 15.1652 3.69379 16 5 16C6.30621 16 7.41745 15.1652 7.82928 14H15C15.5523 14 16 13.5523 16 13C16 12.4477 15.5523 12 15 12H7.82928C7.41745 10.8348 6.30621 10 5 10C3.69379 10 2.58255 10.8348 2.17072 12H1ZM5 14C5.55228 14 6 13.5523 6 13C6 12.4477 5.55228 12 5 12C4.44772 12 4 12.4477 4 13C4 13.5523 4.44772 14 5 14Z"
        fill="currentColor"
      />
    </svg>
  )
}

/**
 *  @group Icons
 */
export const PlusCircleIcon = (props: { large: boolean }) => {
  const className = props.large ? `${defaultDisplay} h-16` : defaultIconClass
  return <HeroPlusCircleIcon className={className} />
}

/**
 *  @group Icons
 */
export const ProfileIcon = ({ className = '' }: { className?: string }) => {
  return (
    <svg
      className={`${defaultDisplay} ${className} p-1`}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="12" r="11" stroke="currentColor" strokeWidth="2" />
      <path
        d="M3 19C4.92729 15.9877 8.23855 14 12 14C15.7614 14 19.0727 15.9877 21 19"
        stroke="currentColor"
        strokeWidth="2"
      />
      <circle cx="12" cy="9.5" r="4.5" stroke="currentColor" strokeWidth="2" />
    </svg>
  )
}

/**
 *  @group Icons
 */
export const EditIcon = ({ color = 'text-green-400', size = 'h-4', lineWidth = 1 }) => {
  return <PencilAltIcon className={`${defaultDisplay} ${size} ${color}`} strokeWidth={lineWidth} />
}

/**
 *  @group Icons
 */

export const CloudUploadIcon = ({ color = 'text-black', size = 'h-4', lineWidth = 1.5 }) => {
  return (
    <HeroCloudUploadIcon className={`${defaultDisplay} ${size} ${color}`} strokeWidth={lineWidth} />
  )
}

export const BookIcon = ({ color = 'text-black', size = 'h-4', lineWidth = 1.5 }) => (
  <HeroBookIcon className={`${defaultDisplay} ${size} ${color}`} strokeWidth={lineWidth} />
)

export const TrashIcon = ({ height = 'h-4', color = 'text-green-400' }) => {
  return <HeroTrashIcon className={`${defaultDisplay} ${height} ${color}`} />
}

/* Mode Icons */

const modeClass = 'h-6'
const modeIconClasses = createVariants({ base: modeClass })

/**
 *  @group Icons
 */
export const ModeDiagnoseIcon = ({ className, ...props }: React.ComponentProps<'svg'>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={modeIconClasses({ className })}
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.00004 18.0001C4.21767 18.0001 0.306495 14.27 0.0171636 9.56045C0.00589396 9.50036 0 9.43837 0 9.375C0 9.34346 0.00146007 9.31226 0.00431632 9.28147C0.00144577 9.18801 0 9.0942 0 9.00004C0 4.02946 4.02946 0 9.00004 0C13.9706 0 18.0001 4.02946 18.0001 9.00004C18.0001 11.1251 17.2636 13.0781 16.0319 14.6178L23.7071 22.293C24.0976 22.6836 24.0976 23.3167 23.707 23.7072C23.3165 24.0978 22.6834 24.0978 22.2928 23.7072L14.6177 16.032C13.0781 17.2636 11.1251 18.0001 9.00004 18.0001ZM9.00004 2C5.34468 2 2.34371 4.8018 2.02753 8.375H2.34716L4.93939 5.34938C5.13299 5.12342 5.41714 4.99541 5.71465 5.00013C6.01216 5.00484 6.29211 5.1418 6.47844 5.37378L10.5332 10.422L12.8831 7.60891C13.0731 7.38146 13.3542 7.25 13.6506 7.25H15.7795C15.0025 4.23085 12.2618 2 9.00004 2ZM2.80723 10.375H2.13499C2.77386 13.5826 5.60464 16.0001 9.00004 16.0001C12.7824 16.0001 15.8641 13.0002 15.9957 9.25H14.1183L11.2855 12.6411C11.0934 12.8711 10.8082 13.0028 10.5085 13C10.2087 12.9971 9.92612 12.8599 9.73843 12.6262L5.67397 7.56597L3.56663 10.0256C3.37665 10.2474 3.09923 10.375 2.80723 10.375Z"
      fill="currentColor"
    />
  </svg>
)

export const ModeScoutingIcon = ({ className, ...props }: React.ComponentProps<'svg'>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={modeIconClasses({ className })}
    viewBox="0 0 24 24"
    fill="none"
    {...props}
  >
    <path
      d="M2 0H1C0.447715 0 0 0.447715 0 1V23C0 23.5523 0.447715 24 1 24C1.55228 24 2 23.5523 2 23V0Z"
      fill="currentColor"
    />
    <path
      d="M4 14H19C19.3558 14 19.6849 13.8109 19.864 13.5035C20.0432 13.196 20.0455 12.8166 19.87 12.507L16.7494 7L19.87 1.49301C20.0455 1.18343 20.0432 0.803956 19.864 0.496518C19.6849 0.189081 19.3558 0 19 0H4V14Z"
      fill="currentColor"
    />
  </svg>
)

export const ModeConfigIcon = ({ className, ...props }: React.ComponentProps<'svg'>) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={modeIconClasses({ className })}
    {...props}
  >
    <path
      d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.5 0H10.5L9.67407 3.30341C8.90234 3.50928 8.17139 3.81519 7.49597 4.20636L4.57544 2.45404L2.4541 4.57532L4.20642 7.49591C3.81519 8.17133 3.50928 8.90234 3.30347 9.67413L0 10.5V13.5L3.30347 14.3259C3.50928 15.0977 3.81519 15.8287 4.20642 16.5041L2.4541 19.4247L4.57532 21.5459L7.49597 19.7936C8.17139 20.1848 8.90234 20.4907 9.67407 20.6966L10.5 24H13.5L14.3258 20.6966C15.0977 20.4907 15.8287 20.1848 16.5042 19.7936L19.4247 21.5459L21.546 19.4246L19.7936 16.504C20.1848 15.8286 20.4907 15.0976 20.6965 14.3259L24 13.5V10.5L20.6965 9.67413C20.4907 8.90234 20.1848 8.17133 19.7936 7.49591L21.5459 4.57538L19.4246 2.4541L16.504 4.20636C15.8286 3.81519 15.0977 3.50934 14.3259 3.30341L13.5 0ZM19 12C19 15.866 15.866 19 12 19C8.13403 19 5 15.866 5 12C5 8.13403 8.13403 5 12 5C15.866 5 19 8.13403 19 12Z"
      fill="currentColor"
    />
  </svg>
)

/**
 *  @group Icons
 */
export const ModeDetailsIcon = ({ className = '' }: { className?: string }) => {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`${modeClass} ${className}`}
    >
      <path
        d="M8 12V18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16 15V18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 9V18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M1 1H18L23 6V23H1V1Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  )
}

const green = 'text-green'
const greenOnDark = 'text-green-300'
const white = 'text-white'

export const GranularIcon = ({ color = green } = {}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    className={`inline-block ${color}`}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4.47273 18.9818C5.89091 18.9818 6.98182 20.0727 6.98182 21.4909C6.98182 22.9091 5.89091 24 4.47273 24C3.05455 24 1.96364 22.9091 1.96364 21.4909C1.96364 20.0727 3.16364 18.9818 4.47273 18.9818ZM13.5273 17.0182C14.9455 17.0182 16.0364 18.1091 16.0364 19.5273C16.0364 20.9455 14.9455 22.0364 13.5273 22.0364C12.1091 22.0364 11.0182 20.9455 11.0182 19.5273C11.0182 18.1091 12.1091 17.0182 13.5273 17.0182ZM7.52727 12.9818C8.94545 12.9818 10.0364 14.0727 10.0364 15.4909C10.0364 16.9091 8.94545 18 7.52727 18C6.10909 18 5.01818 16.9091 5.01818 15.4909C5.01818 14.0727 6.10909 12.9818 7.52727 12.9818ZM16.4727 10.0364C17.8909 10.0364 18.9818 11.1273 18.9818 12.5455C18.9818 13.9636 17.8909 15.0545 16.4727 15.0545C15.0545 15.0545 13.9636 13.9636 13.9636 12.5455C13.9636 11.1273 15.1636 10.0364 16.4727 10.0364ZM2.50909 6.98182C3.92727 6.98182 5.01818 8.07273 5.01818 9.49091C5.01818 10.9091 3.92727 12 2.50909 12C1.09091 12 0 10.9091 0 9.49091C0 8.07273 1.09091 6.98182 2.50909 6.98182ZM10.4727 6C11.8909 6 12.9818 7.09091 12.9818 8.50909C12.9818 9.92727 11.8909 11.0182 10.4727 11.0182C9.05455 11.0182 7.96364 9.92727 7.96364 8.50909C7.96364 7.09091 9.16364 6 10.4727 6ZM21.4909 4.03636C22.9091 4.03636 24 5.12727 24 6.54545C24 7.96364 22.9091 9.05455 21.4909 9.05455C20.0727 9.05455 18.9818 7.96364 18.9818 6.54545C18.9818 5.12727 20.0727 4.03636 21.4909 4.03636ZM5.45455 0.981818C6.87273 0.981818 7.96364 2.07273 7.96364 3.49091C7.96364 4.90909 6.87273 6 5.45455 6C4.03636 6 2.94545 4.90909 2.94545 3.49091C2.94545 2.07273 4.14545 0.981818 5.45455 0.981818ZM15.4909 0C16.9091 0 18 1.09091 18 2.50909C18 3.92727 16.9091 5.01818 15.4909 5.01818C14.0727 5.01818 12.9818 3.92727 12.9818 2.50909C12.9818 1.09091 14.0727 0 15.4909 0Z"
      fill="currentColor"
    />
  </svg>
)

export const LiquidIcon = ({ color = green } = {}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="24"
    viewBox="0 0 18 24"
    fill="none"
    className={`inline-block ${color}`}
  >
    <path
      d="M14 15C14 14.4477 13.5523 14 13 14C12.4477 14 12 14.4477 12 15C12 16.6276 10.7004 18 9 18C8.44771 18 8 18.4477 8 19C8 19.5523 8.44771 20 9 20C11.8401 20 14 17.6967 14 15Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.81781 0.424508C9.63052 0.158362 9.32544 0 9 0C8.67456 0 8.36948 0.158362 8.18219 0.424508L1.53201 9.87477L1.5295 9.87838C1.38937 10.0794 1.25748 10.2865 1.13439 10.4993C0.351637 11.8524 0 13.3974 0 15C0 19.9706 4.02944 24 9 24C13.9706 24 18 19.9706 18 15C18 13.3974 17.6484 11.8524 16.8656 10.4993C16.7425 10.2865 16.6106 10.0794 16.4705 9.87838L9.81781 0.424508ZM3.17016 11.0222L9 2.73764L14.8298 11.0222L14.831 11.0238C14.9388 11.1786 15.0401 11.3376 15.1344 11.5007C15.713 12.501 16 13.6883 16 15C16 18.866 12.866 22 9 22C5.13401 22 2 18.866 2 15C2 13.6883 2.287 12.501 2.86561 11.5007C2.95992 11.3377 3.06114 11.1786 3.1689 11.024L3.17016 11.0222Z"
      fill="currentColor"
    />
  </svg>
)

export const HybridIcon = ({ color = green } = {}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="24"
    viewBox="0 0 20 24"
    fill="none"
    className={`inline-block ${color}`}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M19.236 3.85815C18.2444 1.84165 16.3277 0.515087 13.6374 0.231472C11.1653 -0.0772344 8.68866 -0.077609 6.3091 0.23306C3.59899 0.573183 1.72222 1.99271 0.755515 4.01878C-0.188526 5.99736 -0.211709 8.43595 0.484817 10.802C1.38193 13.9961 2.61749 17.6539 4.33045 20.0132L4.37652 20.0766L4.43191 20.1321C4.54899 20.2494 4.66946 20.3127 4.7111 20.3341C4.76907 20.364 4.82287 20.386 4.86162 20.4008C4.93977 20.4308 5.02207 20.4562 5.09109 20.4762C5.22745 20.5157 5.39194 20.5569 5.53336 20.5923L5.54459 20.5951C5.64699 20.6208 5.73824 20.6437 5.81634 20.6644C6.11711 21.0529 6.54835 21.5637 6.91429 21.9897C7.14795 22.2618 7.36553 22.5113 7.52459 22.6927C7.60418 22.7835 7.66926 22.8574 7.71455 22.9087L7.76702 22.968L7.78591 22.9894C8.9487 24.2997 11.0751 24.3851 12.2019 22.9558L13.0522 21.9436C13.4255 21.499 13.841 21.0036 14.1191 20.6707C14.2022 20.6484 14.3016 20.6234 14.4146 20.5951L14.4258 20.5923C14.5672 20.5569 14.7318 20.5157 14.8681 20.4762C14.9371 20.4562 15.0194 20.4308 15.0976 20.4008C15.1363 20.386 15.1901 20.364 15.2481 20.3341C15.2897 20.3127 15.4102 20.2494 15.5273 20.1321L15.5827 20.0766L15.6287 20.0132C16.3472 19.0235 16.9818 17.8055 17.5425 16.489C17.6178 16.3788 17.6717 16.2527 17.6981 16.1168C18.4039 14.396 18.9885 12.5317 19.4741 10.803C20.2173 8.29855 20.2033 5.82555 19.236 3.85815ZM16.0429 14.8778C16.6134 13.4159 17.1147 11.819 17.5541 10.2543L17.5562 10.2469L17.5583 10.2395C18.2105 8.04656 18.1237 6.12251 17.4448 4.74185C16.7904 3.41094 15.5162 2.43833 13.4201 2.21962L13.4099 2.21855L13.3997 2.21728C11.0842 1.92735 8.77337 1.92803 6.56473 2.2166L6.55944 2.21729C4.47913 2.47777 3.21217 3.50797 2.55695 4.88122C1.87959 6.30086 1.80214 8.20965 2.40148 10.2417L2.40519 10.2542C2.84603 11.8243 3.34931 13.4267 3.92216 14.8927C4.33879 14.8439 4.66121 14.7436 4.93162 14.622C5.33464 14.4406 5.66185 14.1968 6.08237 13.8777L6.10865 13.8578C6.50698 13.5554 6.99567 13.1844 7.6208 12.9032C8.2782 12.6075 9.04877 12.4248 10.0295 12.4248C11.0101 12.4248 11.7849 12.6074 12.4472 12.901C13.0867 13.1844 13.5877 13.5589 13.9978 13.8654L14.0176 13.8803C14.6613 14.3615 15.1783 14.7479 16.0429 14.8778ZM4.74171 16.7803C5.06578 17.4458 5.40868 18.0562 5.77136 18.5894C5.84623 18.6091 5.93261 18.6308 6.02884 18.6549L6.04007 18.6577C6.18149 18.6931 6.34598 18.7343 6.48234 18.7738C6.55136 18.7938 6.63366 18.8192 6.71181 18.8491C6.75056 18.864 6.80437 18.886 6.86233 18.9159C6.90397 18.9373 7.02444 19.0006 7.14152 19.1179L7.19994 19.1764L7.24797 19.2438C7.46638 19.5501 7.95904 20.1396 8.42774 20.6853C8.65579 20.9507 8.86874 21.195 9.02477 21.3729C9.10272 21.4618 9.16633 21.534 9.21034 21.5839L9.26096 21.6412L9.278 21.6604C9.70793 22.1449 10.3648 22.0643 10.6391 21.7111L10.651 21.6957L11.5241 20.6564C11.9863 20.1059 12.5088 19.4827 12.7567 19.1848L12.7856 19.15L12.8177 19.1179C12.9347 19.0006 13.0552 18.9373 13.0969 18.9159C13.1548 18.886 13.2086 18.864 13.2474 18.8491C13.3255 18.8192 13.4078 18.7938 13.4768 18.7738C13.6131 18.7343 13.7775 18.6931 13.9188 18.6577L13.9303 18.6549C14.0266 18.6308 14.113 18.6091 14.1878 18.5894C14.5556 18.0487 14.903 17.4287 15.2311 16.7524C14.1648 16.4861 13.4573 15.9571 12.8399 15.4954L12.8142 15.4762C12.3902 15.1593 12.0519 14.9128 11.6393 14.7299C11.2441 14.5547 10.746 14.4248 10.0295 14.4248C9.31315 14.4248 8.82337 14.5546 8.43878 14.7277C8.03576 14.909 7.70856 15.1528 7.28804 15.4719L7.26176 15.4918C6.86342 15.7942 6.37474 16.1652 5.7496 16.4464C5.44177 16.5849 5.10913 16.6986 4.74171 16.7803Z"
      fill="currentColor"
    />
  </svg>
)

export const HybridSolidIcon = ({ size = 'size-4', color = green }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="16"
    viewBox="0 0 14 16"
    fill="none"
    className={`${size} ${color}`}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.27621 13.6909C8.90654 14.1273 7.7236 15.5091 7.7236 15.5091C7.20606 16.1636 6.17098 16.1636 5.57951 15.5091C5.57951 15.5091 4.39657 14.2 4.0269 13.6909C3.98993 13.6545 3.76813 13.6 3.54633 13.5455C3.32452 13.4909 3.10272 13.4364 3.06576 13.4C2.78979 13.0267 2.52992 12.602 2.28521 12.1406C2.69086 12.0855 3.04071 11.984 3.35521 11.8497C3.8227 11.6501 4.18907 11.3861 4.49573 11.1652L4.51328 11.1525C4.83413 10.9214 5.09375 10.7371 5.41568 10.5997C5.72644 10.467 6.11952 10.3691 6.68608 10.3691C7.25271 10.3691 7.6519 10.467 7.97053 10.6011C8.29964 10.7395 8.56761 10.9255 8.89143 11.1553L8.90837 11.1673C9.44164 11.5457 10.0464 11.9749 11.0242 12.1287C10.7777 12.5947 10.5157 13.0235 10.2374 13.4C10.2004 13.4364 9.97859 13.4909 9.75678 13.5455C9.53498 13.6 9.31318 13.6545 9.27621 13.6909ZM11.6282 10.8502C12.1552 9.61131 12.5991 8.23485 12.9729 6.92727C14.008 3.50909 12.825 0.527273 9.27621 0.163636C7.5018 -0.0545455 5.72738 -0.0545455 4.0269 0.163636C0.478065 0.6 -0.630945 3.72727 0.330197 6.92727C0.704293 8.23568 1.1485 9.61306 1.67595 10.8526C2.18157 10.8403 2.54227 10.747 2.8316 10.6235C3.15353 10.486 3.41316 10.3017 3.734 10.0706L3.75156 10.058C4.05821 9.83704 4.42459 9.57308 4.89208 9.37346C5.37969 9.16524 5.9523 9.03581 6.68608 9.03581C7.41981 9.03581 7.99588 9.16521 8.48757 9.37207C8.96616 9.57341 9.34209 9.84016 9.65781 10.0642L9.67412 10.0758C10.2461 10.4817 10.7114 10.8118 11.6282 10.8502Z"
      fill="currentColor"
    />
  </svg>
)

export const Gen3DisplayIcon = ({ onDark = false, colorWhite = false } = {}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    className={`inline-block ${colorWhite ? white : onDark ? greenOnDark : green}`}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.4072 0.478013C0.906122 0.68358 0 1.95705 0 3.47214V14.5279C0 16.043 0.906124 17.3164 2.4072 17.522C4.17767 17.7644 7.14663 18 12 18C16.8534 18 19.8223 17.7644 21.5928 17.522C23.0939 17.3164 24 16.043 24 14.5279V3.47214C24 1.95705 23.0939 0.68358 21.5928 0.478012C19.8223 0.235552 16.8534 0 12 0C7.14663 0 4.17767 0.235552 2.4072 0.478013ZM22 16H2V2H22V16Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 19.75C7 19.75 4 19.5 4 19.5L4.65197 22.4339C4.85532 23.3489 5.66695 24 6.60434 24H17.3957C18.3331 24 19.1447 23.3489 19.348 22.4339L20 19.5C20 19.5 17 19.75 12 19.75ZM7 21C6.44772 21 6 21.4477 6 22C6 22.5523 6.44772 23 7 23C7.55228 23 8 22.5523 8 22C8 21.4477 7.55228 21 7 21ZM16 22C16 21.4477 16.4477 21 17 21C17.5523 21 18 21.4477 18 22C18 22.5523 17.5523 23 17 23C16.4477 23 16 22.5523 16 22ZM11 21C10.4477 21 10 21.4477 10 22C10 22.5523 10.4477 23 11 23H13C13.5523 23 14 22.5523 14 22C14 21.4477 13.5523 21 13 21H11Z"
      fill="currentColor"
    />
  </svg>
)

export const DataSharingIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="26"
    height="25"
    stroke="currentColor"
    viewBox="0 0 26 25"
    fill="none"
    className="w-6 text-green"
  >
    <circle cx="12.7394" cy="13.6012" r="9.60514" strokeWidth="2" />
    <ellipse
      cx="12.9999"
      cy="4.25607"
      rx="3.20171"
      ry="3.20171"
      transform="rotate(-4.87265 12.9999 4.25607)"
      fill="white"
      strokeWidth="2"
    />
    <ellipse
      cx="21.5381"
      cy="18.1321"
      rx="3.20171"
      ry="3.20171"
      transform="rotate(-4.87265 21.5381 18.1321)"
      fill="white"
      strokeWidth="2"
    />
    <ellipse
      cx="4.4621"
      cy="18.1321"
      rx="3.20171"
      ry="3.20171"
      transform="rotate(-4.87265 4.4621 18.1321)"
      fill="white"
      strokeWidth="2"
    />
  </svg>
)

export const ManageOperationIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-6 text-green"
    stroke="currentColor"
    width="26"
    height="26"
    viewBox="0 0 26 26"
    fill="none"
  >
    <rect x="1" y="1" width="24" height="24" strokeWidth="2" strokeLinejoin="round" />
    <path d="M12.9999 19L5.99988 19" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M20.4761 13L5.99988 13" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M20.4761 7L5.99988 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

export const UsersIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    stroke="currentColor"
    width="26"
    height="18"
    viewBox="0 0 26 18"
    fill="none"
    className="w-6 text-green"
  >
    <path
      d="M17.2019 9.43262C13.9428 9.43262 11.0737 12.1882 9.40381 16.3643H25.0001C23.3302 12.1882 20.4611 9.43262 17.2019 9.43262Z"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <ellipse cx="17.2016" cy="5.53481" rx="3.89907" ry="3.89907" strokeWidth="2" />
    <path
      d="M13.103 16.3645H1C2.29589 13.1237 4.52234 10.9854 7.0515 10.9854C8.51691 10.9854 9.88071 11.7032 11.0208 12.9375"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <ellipse cx="7.05138" cy="7.96032" rx="3.02575" ry="3.02575" strokeWidth="2" />
  </svg>
)

export const LiveDataIndicator = () => (
  <span className="relative inline-block size-3">
    <span className="absolute top-0 size-3 animate-ping rounded-full bg-green-400" />
    <span className="absolute top-0 size-3 rounded-full bg-[--panorama-platform-accent]" />
  </span>
)

export const ClientIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="size-4 text-green"
  >
    <path
      id="vector"
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8ZM10.3333 5C10.3333 6.28866 9.28866 7.33333 8 7.33333C6.71134 7.33333 5.66667 6.28866 5.66667 5C5.66667 3.71134 6.71134 2.66667 8 2.66667C9.28866 2.66667 10.3333 3.71134 10.3333 5ZM8 14C10.2091 14 12 11.8815 12 10.3167C12 8.75186 10.2091 8.33333 8 8.33333C5.79086 8.33333 4 8.75186 4 10.3167C4 11.8815 5.79086 14 8 14Z"
      fill="currentColor"
    />
  </svg>
)

export const FarmIcon = ({ size = 'size-4', color = 'text-green' }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${size} ${color}`}
  >
    <g id="vector">
      <path
        d="M8 2.45486C8 2.37961 8.03997 2.31002 8.10497 2.2721L12 0L15.8977 2.27364C15.961 2.3106 16 2.37845 16 2.45181C16 2.56564 15.9078 2.65795 15.794 2.65808L15.3333 2.65859V12.6667H12V8.1849C12 7.61003 11.7039 7.07617 11.2166 6.77148L8.66667 5.17773V2.66593L8.21181 2.66643C8.09487 2.66656 8 2.5718 8 2.45486Z"
        fill="currentColor"
      />
      <path
        d="M0.156667 7.90208C0.0592066 7.963 0 8.06982 0 8.18475V12.3329C0 12.5172 0.14949 12.6665 0.333746 12.6663L10.3337 12.6539C10.5177 12.6536 10.6667 12.5045 10.6667 12.3205V8.18475C10.6667 8.06982 10.6075 7.963 10.51 7.90208L5.51 4.77708C5.40191 4.70953 5.26476 4.70953 5.15667 4.77708L0.156667 7.90208Z"
        fill="currentColor"
      />
      <path
        d="M4 14C3.2 14 2.4 15.3333 2.4 15.3333L2 16H0C0.8 14.6667 1.53333 14 2.33333 14H4Z"
        fill="currentColor"
      />
      <path
        d="M6.4 15.3333C6.4 15.3333 7.2 14 8 14H6.33333C5.53333 14 4.8 14.6667 4 16H6L6.4 15.3333Z"
        fill="currentColor"
      />
      <path
        d="M12 14C11.2 14 10.4 15.3333 10.4 15.3333L10 16H8C8.8 14.6667 9.53333 14 10.3333 14H12Z"
        fill="currentColor"
      />
      <path
        d="M14.4 15.3333C14.4 15.3333 15.2 14 16 14H14.3333C13.5333 14 12.8 14.6667 12 16H14L14.4 15.3333Z"
        fill="currentColor"
      />
    </g>
  </svg>
)

export const PlanningIcon = ({ size = 'size-4', className = 'text-green' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    className={`${size} ${className}`}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11 5.5C11 8.53757 8.5376 11 5.5 11C2.4624 11 0 8.53757 0 5.5C0 2.46243 2.4624 0 5.5 0C8.5376 0 11 2.46243 11 5.5ZM8.30469 2.66793C8.76428 2.97429 8.88843 3.59515 8.58203 4.05468L5.58203 8.55468C5.41589 8.80396 5.14673 8.9656 4.84851 8.99512C4.55042 9.02464 4.25476 8.91893 4.04285 8.70709L2.04285 6.70709C1.65234 6.31657 1.65234 5.6834 2.04285 5.29288C2.43347 4.90235 3.06653 4.90235 3.45715 5.29288L4.5946 6.43035L6.91797 2.94529C7.22424 2.48576 7.84521 2.36158 8.30469 2.66793Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11 18.5C11 21.5376 8.5376 24 5.5 24C2.4624 24 0 21.5376 0 18.5C0 15.4624 2.4624 13 5.5 13C8.5376 13 11 15.4624 11 18.5ZM8.30469 15.6679C8.76428 15.9743 8.88843 16.5952 8.58203 17.0547L5.58203 21.5547C5.41589 21.804 5.14673 21.9656 4.84851 21.9951C4.55042 22.0246 4.25476 21.9189 4.04285 21.7071L2.04285 19.7071C1.65234 19.3166 1.65234 18.6834 2.04285 18.2929C2.43347 17.9024 3.06653 17.9024 3.45715 18.2929L4.5946 19.4303L6.91797 15.9453C7.22424 15.4858 7.84521 15.3616 8.30469 15.6679Z"
      fill="currentColor"
    />
    <path
      d="M14 20C14 19.4477 14.4478 19 15 19H21C21.5522 19 22 19.4477 22 20C22 20.5523 21.5522 21 21 21H15C14.4478 21 14 20.5523 14 20Z"
      fill="currentColor"
    />
    <path
      d="M15 15C14.4478 15 14 15.4477 14 16C14 16.5523 14.4478 17 15 17H23C23.5522 17 24 16.5523 24 16C24 15.4477 23.5522 15 23 15H15Z"
      fill="currentColor"
    />
    <path
      d="M14 4C14 3.44772 14.4478 3 15 3H23C23.5522 3 24 3.44772 24 4C24 4.55228 23.5522 5 23 5H15C14.4478 5 14 4.55228 14 4Z"
      fill="currentColor"
    />
    <path
      d="M15 7C14.4478 7 14 7.44772 14 8C14 8.55228 14.4478 9 15 9H21C21.5522 9 22 8.55228 22 8C22 7.44772 21.5522 7 21 7H15Z"
      fill="currentColor"
    />
  </svg>
)

export const PlanningFieldsIcon = ({
  size = 'size-4',
  color = 'text-green'
}: {
  size?: string
  color?: string
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    className={`${size} ${color}`}
  >
    <path
      d="M23.3172 8C21.6698 3.33923 17.2249 0 12 0C6.77512 0 2.33017 3.33923 0.682823 8H23.3172Z"
      fill="currentColor"
    />
    <path
      d="M0.00230086 12.2374C0.000769305 12.1584 0 12.0793 0 12C0 11.3186 0.0567976 10.6504 0.165917 10H4C3.02892 10.2266 1.51467 10.9108 0.00230086 12.2374Z"
      fill="currentColor"
    />
    <path
      d="M1.3289 17.4943C0.836555 16.5401 0.468466 15.5112 0.246017 14.4292C1.19457 13.0861 2.36721 11.8865 3.78478 11.013C5.16267 10.164 6.82329 10 8.44178 10H9C7.15741 10.4299 3.3592 12.5077 1.3289 17.4943Z"
      fill="currentColor"
    />
    <path
      d="M5.18494 21.8783C4.27084 21.2465 3.44872 20.491 2.74273 19.6362C3.70562 16.4842 5.64403 12.9483 8.78478 11.013C10.1627 10.164 11.8233 10 13.4418 10H14C11.6893 10.5392 6.30336 13.6696 5.18494 21.8783Z"
      fill="currentColor"
    />
    <path
      d="M10.0073 23.8353C8.97299 23.6624 7.98351 23.3573 7.05681 22.9378C7.4123 19.3852 9.45717 13.6796 13.7848 11.013C15.1627 10.164 16.8233 10 18.4418 10H19C16.5141 10.5801 10.4685 14.1594 10.0073 23.8353Z"
      fill="currentColor"
    />
    <path
      d="M18.6356 22C16.735 23.2637 14.4535 24 12 24C12 23.337 12.0427 22.6678 12.1282 22H18.6356Z"
      fill="currentColor"
    />
    <path
      d="M22.3946 18C21.9799 18.7168 21.4932 19.3868 20.9444 20H12.5149C12.6917 19.3213 12.914 18.6519 13.1818 18H22.3946Z"
      fill="currentColor"
    />
    <path
      d="M23.8341 14C23.7186 14.6883 23.5445 15.3567 23.3172 16H14.1843C14.6089 15.2935 15.0955 14.6225 15.644 14H23.8341Z"
      fill="currentColor"
    />
    <path
      d="M23.8346 10.0031C23.9434 10.6525 24 11.3197 24 12H17.8727C18.7691 11.3713 19.768 10.8486 20.8695 10.4594C21.8188 10.124 22.8269 10.0212 23.8346 10.0031Z"
      fill="currentColor"
    />
  </svg>
)

export const BoundaryIcon = ({ size = 'size-4', color = 'text-white' }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${size} ${color}`}
  >
    <path
      d="M14.667 0C15.4031 0.000175696 15.9998 0.596886 16 1.33301C16 1.82653 15.7315 2.25774 15.333 2.48828V13.5117C15.7315 13.7423 16 14.1735 16 14.667C15.9998 15.4031 15.4031 15.9998 14.667 16C14.1735 16 13.7423 15.7315 13.5117 15.333H2.48828C2.25774 15.7315 1.82653 16 1.33301 16C0.596886 15.9998 0.000175696 15.4031 0 14.667C0 14.1735 0.268458 13.7423 0.666992 13.5117V6.48828C0.268458 6.25774 0 5.82653 0 5.33301C0.000175954 4.59689 0.596886 4.00018 1.33301 4C1.51338 4 1.68585 4.03568 1.84277 4.10059L5.33691 1.42871C5.33469 1.3972 5.33301 1.36508 5.33301 1.33301C5.33318 0.596778 5.93072 0 6.66699 0C7.16038 0.00011776 7.5908 0.268545 7.82129 0.666992H13.5117C13.7423 0.268458 14.1735 0 14.667 0ZM1.33301 14C0.964968 14.0002 0.666992 14.2989 0.666992 14.667C0.667168 15.0349 0.965076 15.3328 1.33301 15.333C1.70109 15.333 1.99982 15.035 2 14.667C2 14.2988 1.7012 14 1.33301 14ZM14.667 14C14.2988 14 14 14.2988 14 14.667C14.0002 15.035 14.2989 15.333 14.667 15.333C15.0349 15.3328 15.3328 15.0349 15.333 14.667C15.333 14.2989 15.035 14.0002 14.667 14ZM7.82129 2C7.59078 2.39837 7.16032 2.66687 6.66699 2.66699C6.45898 2.66699 6.26139 2.61914 6.08594 2.53418L2.65527 5.1582C2.66278 5.21541 2.66698 5.27376 2.66699 5.33301C2.66699 5.82653 2.39853 6.25774 2 6.48828V13.5117C2.20242 13.6288 2.37119 13.7976 2.48828 14H13.5117C13.6288 13.7976 13.7976 13.6288 14 13.5117V2.48828C13.7976 2.37119 13.6288 2.20242 13.5117 2H7.82129ZM4 5.66699C4.36808 5.66699 4.66682 5.96497 4.66699 6.33301V12.333C4.66699 12.7012 4.36819 13 4 13C3.63181 13 3.33301 12.7012 3.33301 12.333V6.33301C3.33318 5.96497 3.63192 5.66699 4 5.66699ZM6.66699 4C7.03503 4.00018 7.33301 4.29891 7.33301 4.66699V12.333C7.33301 12.7011 7.03503 12.9998 6.66699 13C6.2988 13 6 12.7012 6 12.333V4.66699C6 4.2988 6.2988 4 6.66699 4ZM9.33301 3C9.7012 3 10 3.2988 10 3.66699V12.333C10 12.7012 9.7012 13 9.33301 13C8.96497 12.9998 8.66699 12.7011 8.66699 12.333V3.66699C8.66699 3.29891 8.96497 3.00018 9.33301 3ZM12 3C12.3682 3 12.667 3.2988 12.667 3.66699V12.333C12.667 12.7012 12.3682 13 12 13C11.6318 13 11.333 12.7012 11.333 12.333V3.66699C11.333 3.2988 11.6318 3 12 3ZM1.33301 4.66699C0.965077 4.66717 0.667168 4.96508 0.666992 5.33301C0.666992 5.70109 0.964968 5.99982 1.33301 6C1.7012 6 2 5.7012 2 5.33301C1.99982 4.96497 1.70109 4.66699 1.33301 4.66699ZM6.66699 0.666992C6.29891 0.666992 6.00018 0.964968 6 1.33301C6 1.7012 6.2988 2 6.66699 2C7.03504 1.99982 7.33301 1.70109 7.33301 1.33301C7.33283 0.965076 7.03493 0.667168 6.66699 0.666992ZM14.667 0.666992C14.2989 0.666992 14.0002 0.964968 14 1.33301C14 1.7012 14.2988 2 14.667 2C15.035 1.99982 15.333 1.70109 15.333 1.33301C15.3328 0.965076 15.0349 0.667168 14.667 0.666992Z"
      fill="currentColor"
    />
  </svg>
)

export const EditBoundaryIcon = ({ className }: { className: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    className={className}
  >
    <path
      d="M10 7.45058e-09C10.7403 7.45058e-09 11.3866 0.402202 11.7324 1H15C15.5523 1 16 1.44772 16 2C16 2.55228 15.5523 3 15 3H11.7324C11.3866 3.5978 10.7403 4 10 4C9.68806 4 9.39301 3.92815 9.12988 3.80078L3.98242 7.73633C3.99375 7.82247 4 7.91077 4 8C4 8.74028 3.5978 9.38661 3 9.73242V20.2676C3.30362 20.4432 3.55678 20.6964 3.73242 21H20.2676C20.4432 20.6964 20.6964 20.4432 21 20.2676V9C21 8.44772 21.4477 8 22 8C22.5523 8 23 8.44772 23 9V20.2676C23.5978 20.6134 24 21.2597 24 22C24 23.1046 23.1046 24 22 24C21.2597 24 20.6134 23.5978 20.2676 23H3.73242C3.38661 23.5978 2.74028 24 2 24C0.895431 24 0 23.1046 0 22C0 21.2597 0.402202 20.6134 1 20.2676V9.73242C0.402202 9.38661 0 8.74028 0 8C0 6.89543 0.895431 6 2 6C2.27051 6 2.52833 6.05404 2.76367 6.15137L8.00488 2.14355C8.00154 2.09626 8 2.04815 8 2C8 0.895431 8.89543 7.45058e-09 10 7.45058e-09ZM2 21C1.44772 21 1 21.4477 1 22C1 22.5523 1.44772 23 2 23C2.55228 23 3 22.5523 3 22C3 21.4477 2.55228 21 2 21ZM22 21C21.4477 21 21 21.4477 21 22C21 22.5523 21.4477 23 22 23C22.5523 23 23 22.5523 23 22C23 21.4477 22.5523 21 22 21ZM2 7C1.44772 7 1 7.44772 1 8C1 8.55228 1.44772 9 2 9C2.55228 9 3 8.55228 3 8C3 7.44772 2.55228 7 2 7ZM10 1C9.44771 1 9 1.44772 9 2C9 2.55228 9.44771 3 10 3C10.5523 3 11 2.55228 11 2C11 1.44772 10.5523 1 10 1Z"
      fill="currentColor"
    />
    <path
      d="M17.5725 3.68757L8.76992 12.4902C8.69814 12.5619 8.64921 12.6534 8.6293 12.7529L8.0045 15.8769C7.9901 15.9489 8.05355 16.0123 8.12551 15.9979L11.2495 15.3731C11.3491 15.3532 11.4405 15.3043 11.5123 15.2325L20.3149 6.42993L17.5725 3.68757Z"
      fill="currentColor"
    />
    <path
      d="M23.7012 1.58921L22.4132 0.301207C22.0116 -0.100403 21.3605 -0.100402 20.9589 0.301208L18.9437 2.31639L21.6861 5.05875L23.7012 3.04357C24.1028 2.64196 24.1028 1.99082 23.7012 1.58921Z"
      fill="currentColor"
    />
  </svg>
)

export const MagicBoundaryIcon = (props: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    {...props}
  >
    (
    <path
      d="M14.6667 0C15.403 0 16 0.596954 16 1.33333C16 1.82685 15.7319 2.25774 15.3333 2.48828V13.5117C15.7319 13.7423 16 14.1731 16 14.6667C16 15.403 15.403 16 14.6667 16C14.1731 16 13.7423 15.7319 13.5117 15.3333H2.48828C2.25774 15.7319 1.82685 16 1.33333 16C0.596954 16 0 15.403 0 14.6667C0 14.1731 0.268135 13.7423 0.666667 13.5117V6.48828C0.268135 6.25774 0 5.82685 0 5.33333C0 4.59695 0.596954 4 1.33333 4C1.51367 4 1.68555 4.03603 1.84245 4.10091L5.33659 1.42904C5.33436 1.3975 5.33333 1.36543 5.33333 1.33333C5.33333 0.596954 5.93029 0 6.66667 0C7.16019 0 7.59107 0.268135 7.82161 0.666667H13.5117C13.7423 0.268135 14.1731 0 14.6667 0ZM1.33333 14C0.965144 14 0.666667 14.2985 0.666667 14.6667C0.666667 15.0349 0.965144 15.3333 1.33333 15.3333C1.70152 15.3333 2 15.0349 2 14.6667C2 14.2985 1.70152 14 1.33333 14ZM14.6667 14C14.2985 14 14 14.2985 14 14.6667C14 15.0349 14.2985 15.3333 14.6667 15.3333C15.0349 15.3333 15.3333 15.0349 15.3333 14.6667C15.3333 14.2985 15.0349 14 14.6667 14ZM7.82161 2C7.59107 2.39853 7.16019 2.66667 6.66667 2.66667C6.45871 2.66667 6.262 2.61877 6.08659 2.53385L2.65495 5.15755C2.6625 5.21498 2.66667 5.27385 2.66667 5.33333C2.66667 5.82685 2.39853 6.25774 2 6.48828V13.5117C2.20242 13.6288 2.37119 13.7976 2.48828 14H13.5117C13.6288 13.7976 13.7976 13.6288 14 13.5117V2.48828C13.7976 2.37119 13.6288 2.20242 13.5117 2H7.82161ZM1.33333 4.66667C0.965144 4.66667 0.666667 4.96514 0.666667 5.33333C0.666667 5.70152 0.965144 6 1.33333 6C1.70152 6 2 5.70152 2 5.33333C2 4.96514 1.70152 4.66667 1.33333 4.66667ZM6.66667 0.666667C6.29847 0.666667 6 0.965144 6 1.33333C6 1.70152 6.29847 2 6.66667 2C7.03486 2 7.33333 1.70152 7.33333 1.33333C7.33333 0.965144 7.03486 0.666667 6.66667 0.666667ZM14.6667 0.666667C14.2985 0.666667 14 0.965144 14 1.33333C14 1.70152 14.2985 2 14.6667 2C15.0349 2 15.3333 1.70152 15.3333 1.33333C15.3333 0.965144 15.0349 0.666667 14.6667 0.666667Z"
      fill="currentColor"
    />
    ){' '}
    <path
      d="M8 4C8.13948 4 8.2641 4.08696 8.3125 4.21777L9.24967 6.75L11.7822 7.6875L11.8291 7.70898C11.9334 7.76718 12 7.87791 12 8C12 8.12209 11.9334 8.23283 11.8291 8.29102L11.7822 8.3125L9.24967 9.24967L8.3125 11.7822C8.2641 11.913 8.13948 12 8 12C7.87791 12 7.76718 11.9334 7.70898 11.8291L7.6875 11.7822L6.75 9.24967L4.21777 8.3125C4.08696 8.2641 4 8.13948 4 8C4 7.86052 4.08696 7.7359 4.21777 7.6875L6.75 6.75L7.6875 4.21777L7.70898 4.1709C7.76718 4.06657 7.87791 4 8 4ZM7.32227 7.12533C7.2885 7.21657 7.21657 7.2885 7.12533 7.32227L5.29329 8L7.12533 8.67773L7.15853 8.69206C7.23393 8.72968 7.29271 8.7948 7.32227 8.87467L8 10.7064L8.67773 8.87467L8.69206 8.84147C8.72968 8.76607 8.7948 8.70729 8.87467 8.67773L10.7064 8L8.87467 7.32227C8.78343 7.2885 8.7115 7.21657 8.67773 7.12533L8 5.29329L7.32227 7.12533Z"
      fill="currentColor"
    />
    <path
      d="M7.32227 7.12533C7.2885 7.21657 7.21657 7.2885 7.12533 7.32227L5.29329 8L7.12533 8.67773L7.15853 8.69206C7.23393 8.72968 7.29271 8.7948 7.32227 8.87467L8 10.7064L8.67773 8.87467L8.69206 8.84147C8.72968 8.76607 8.7948 8.70729 8.87467 8.67773L10.7064 8L8.87467 7.32227C8.78343 7.2885 8.7115 7.21657 8.67773 7.12533L8 5.29329L7.32227 7.12533Z"
      fill="currentColor"
    />
    <path
      d="M11 10L11.1801 10.4866L11.6667 10.6667L11.1801 10.8467L11 11.3333L10.8199 10.8467L10.3333 10.6667L10.8199 10.4866L11 10Z"
      fill="currentColor"
    />
    <path
      d="M11 9.66667C11.1395 9.66667 11.2641 9.75363 11.3125 9.88444L11.4395 10.2269L11.7822 10.3542L11.8291 10.3757C11.9334 10.4338 12 10.5446 12 10.6667C12 10.7888 11.9334 10.8995 11.8291 10.9577L11.7822 10.9792L11.4395 11.1061L11.3125 11.4489C11.2641 11.5797 11.1395 11.6667 11 11.6667C10.8779 11.6667 10.7672 11.6001 10.709 11.4958L10.6875 11.4489L10.5602 11.1061L10.2178 10.9792C10.087 10.9308 10 10.8061 10 10.6667C10 10.5272 10.087 10.4026 10.2178 10.3542L10.5602 10.2269L10.6875 9.88444L10.709 9.83757C10.7672 9.73324 10.8779 9.66667 11 9.66667Z"
      fill="currentColor"
    />
    <path
      d="M11 5L11.1801 5.48661L11.6667 5.66667L11.1801 5.84673L11 6.33333L10.8199 5.84673L10.3333 5.66667L10.8199 5.48661L11 5Z"
      fill="currentColor"
    />
    <path
      d="M11 4.66667C11.1395 4.66667 11.2641 4.75363 11.3125 4.88444L11.4395 5.22689L11.7822 5.35417L11.8291 5.37565C11.9334 5.43384 12 5.54457 12 5.66667C12 5.78876 11.9334 5.89949 11.8291 5.95768L11.7822 5.97917L11.4395 6.10612L11.3125 6.44889C11.2641 6.5797 11.1395 6.66667 11 6.66667C10.8779 6.66667 10.7672 6.60009 10.709 6.49577L10.6875 6.44889L10.5602 6.10612L10.2178 5.97917C10.087 5.93076 10 5.80615 10 5.66667C10 5.52719 10.087 5.40257 10.2178 5.35417L10.5602 5.22689L10.6875 4.88444L10.709 4.83756C10.7672 4.73324 10.8779 4.66667 11 4.66667Z"
      fill="currentColor"
    />
  </svg>
)

export const EraseIcon = ({ className }: { className: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="22"
    viewBox="0 0 24 22"
    fill="none"
    className={className}
  >
    <path
      d="M13.0107 1.28121C14.1522 0.204381 15.9312 0.241041 17.0302 1.38961L21.7236 6.29489L21.9189 6.52047C22.7691 7.60823 22.7692 9.16714 21.9189 10.2548L21.7236 10.4804L13.0927 19.5H23C23.5522 19.5 24 19.9477 24 20.5C24 21.0522 23.5522 21.5 23 21.5H4.42867C4.15589 21.5 3.89465 21.3884 3.70602 21.1914L0.832971 18.1894C-0.277657 17.0287 -0.277657 15.1636 0.832971 14.0029L12.9023 1.38961L13.0107 1.28121ZM2.27828 15.3857C1.90759 15.7731 1.90759 16.4192 2.27828 16.8066L4.85543 19.5H10.3252L15.5937 13.9931L9.60153 7.73043L2.27828 15.3857ZM15.5849 2.77145C15.2598 2.43178 14.7617 2.41073 14.415 2.70797L14.3476 2.77145L10.9853 6.28512L16.9775 12.5478L20.2783 9.09762L20.3437 9.02242C20.6277 8.65697 20.6276 8.11841 20.3437 7.75289L20.2783 7.6777L15.5849 2.77145Z"
      fill="currentColor"
    />
  </svg>
)

export const SplitIcon = ({ className }: { className: string }) => (
  <svg
    width="24"
    height="20"
    viewBox="0 0 24 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M19.5403 14.8574C20.0564 15.0541 20.6346 14.7953 20.8314 14.2793C21.028 13.7633 20.7691 13.1851 20.2532 12.9883L6.44073 7.72362C6.88653 7.34393 7.25752 6.85801 7.513 6.28417C8.40659 4.27688 7.59518 1.85659 5.61749 0.890614C3.61224 -0.0887769 1.26543 0.840394 0.36163 2.87011C-0.532025 4.8774 0.279435 7.29766 2.25714 8.26366C2.45841 8.36196 2.66309 8.44128 2.86944 8.50194L19.5403 14.8574ZM5.68585 5.47069C5.27607 6.39112 4.34255 6.81499 3.48858 6.59764L3.1087 6.45311C2.16413 5.97332 1.71572 4.74616 2.18878 3.68358C2.65623 2.63395 3.80462 2.23097 4.73956 2.68749C5.70213 3.15762 6.16322 4.39828 5.68585 5.47069Z"
      fill="currentColor"
    />
    <path
      d="M19.5403 5.14279C20.0564 4.94608 20.6346 5.20492 20.8314 5.72091C21.028 6.23686 20.7691 6.81508 20.2532 7.01193L6.44073 12.2766C6.88653 12.6563 7.25752 13.1422 7.513 13.716C8.40659 15.7233 7.59518 18.1436 5.61749 19.1096C3.61224 20.089 1.26543 19.1598 0.36163 17.1301C-0.532025 15.1228 0.279435 12.7025 2.25714 11.7365C2.45841 11.6382 2.66309 11.5589 2.86944 11.4983L19.5403 5.14279ZM5.68585 14.5295C5.27607 13.6091 4.34255 13.1852 3.48858 13.4026L3.1087 13.5471C2.16413 14.0269 1.71572 15.254 2.18878 16.3166C2.65623 17.3662 3.80462 17.7692 4.73956 17.3127C5.70213 16.8426 6.16322 15.6019 5.68585 14.5295Z"
      fill="currentColor"
    />
    <path
      d="M20.9222 9.99991C20.9222 10.5721 20.463 11.0359 19.8965 11.0359C19.33 11.0359 18.8708 10.5721 18.8708 9.99991C18.8708 9.42772 19.33 8.96388 19.8965 8.96388C20.463 8.96388 20.9222 9.42772 20.9222 9.99991Z"
      fill="currentColor"
    />
    <path
      d="M17.8454 9.99991C17.8454 10.5721 17.3862 11.0359 16.8197 11.0359C16.2532 11.0359 15.794 10.5721 15.794 9.99991C15.794 9.42772 16.2532 8.96388 16.8197 8.96388C17.3862 8.96388 17.8454 9.42772 17.8454 9.99991Z"
      fill="currentColor"
    />
    <path
      d="M23.9993 9.99991C23.9993 10.5721 23.5401 11.0359 22.9737 11.0359C22.4072 11.0359 21.948 10.5721 21.948 9.99991C21.948 9.42772 22.4072 8.96388 22.9737 8.96388C23.5401 8.96388 23.9993 9.42772 23.9993 9.99991Z"
      fill="currentColor"
    />
  </svg>
)

export const PrescriptionIcon = ({ size = 'size-4', color = 'text-white' }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${size} ${color}`}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.28205 6.55555C8.44481 6.11688 9.49851 5.33579 9.77548 4.13479C9.81457 3.96146 9.83793 3.78368 9.84434 3.60244C9.84554 3.56841 9.84615 3.53426 9.84615 3.5C9.84615 1.56702 7.91752 0 5.53846 0H0V11H2.46154V7H4.61538L8.30769 11L3.69231 16H6.76923L9.84615 12.6667L12.9231 16H16L11.3846 11L16 6H12.9231L9.84615 9.33333L7.28205 6.55555ZM5.53846 2H2.46154V5H5.53846C6.55807 5 7.38461 4.32843 7.38461 3.5C7.38461 2.67157 6.55807 2 5.53846 2Z"
      fill="currentColor"
    />
  </svg>
)

export const PrescriptionWithBorderIcon = ({ size = 'size-4', color = 'text-white' }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${size} ${color}`}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.9167 10.5555C12.8614 10.1169 13.7175 9.33579 13.9426 8.13478C13.9743 7.96146 13.9933 7.78368 13.9985 7.60244C13.9995 7.56841 14 7.53426 14 7.5C14 5.56702 12.433 4 10.5 4H6V15H8V11H9.75L12.75 15L9 20H11.5L14 16.6667L16.5 20H19L15.25 15L19 10H16.5L14 13.3333L11.9167 10.5555ZM10.5 6H8V9H10.5C11.3284 9 12 8.32843 12 7.5C12 6.67157 11.3284 6 10.5 6Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 0V24H24V0H0ZM2 2V22H22V2H2Z"
      fill="currentColor"
    />
  </svg>
)

export const TankLevelIcon = ({ size = 'size-4' }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${size} text-white`}
  >
    <path
      d="M6.33329 3.66667C6.33329 3.29846 6.63175 3 6.99996 3H8.99996C9.36816 3 9.66663 3.29846 9.66663 3.66667C9.66663 4.03487 9.36816 4.33333 8.99996 4.33333H6.99996C6.63175 4.33333 6.33329 4.03487 6.33329 3.66667Z"
      fill="currentColor"
    />
    <path
      d="M6.99996 5.66667C6.63175 5.66667 6.33329 5.96513 6.33329 6.33333C6.33329 6.70154 6.63175 7 6.99996 7H8.99996C9.36816 7 9.66663 6.70154 9.66663 6.33333C9.66663 5.96513 9.36816 5.66667 8.99996 5.66667H6.99996Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8 16C12.1422 16 15.549 12.852 15.9587 8.81795C15.986 8.54903 16 8.27612 16 8C16 3.58171 12.4183 0 8 0C3.58171 0 0 3.58171 0 8C0 9.13867 0.237874 10.2218 0.666667 11.2023C1.90169 14.0265 4.72038 16 8 16ZM14.6667 8C14.6667 8.43713 14.6667 8.66667 14.6667 8.66667C14.6667 8.66667 14.2045 8.66667 14 8.66667C13.1667 8.66667 14 8.66667 12.3333 8.66667C11.6667 8.66667 11.5 8.66667 10.6667 8.66667C9.83333 8.66667 9.45798 8.66667 9 8.66667C8.33333 8.66667 8.16667 8.66667 7.33333 8.66667C6.5 8.66667 5.88037 8.66667 5.33333 8.66667C5 8.66667 4.83333 8.66667 4 8.66667C3.16667 8.66667 3 8.66667 2.33333 8.66667C2 8.66667 1.66667 8.66667 1.33333 8.66667C1.33333 8.33333 1.33333 8.41414 1.33333 8C1.33333 4.31812 4.31812 1.33333 8 1.33333C11.6819 1.33333 14.6667 4.31812 14.6667 8ZM6.33333 10.6667C6.33333 10.2985 6.6318 10 7 10H9C9.36821 10 9.66667 10.2985 9.66667 10.6667C9.66667 11.0349 9.36821 11.3333 9 11.3333H7C6.6318 11.3333 6.33333 11.0349 6.33333 10.6667ZM6.33333 13.3333C6.33333 12.9651 6.6318 12.6667 7 12.6667H9C9.36821 12.6667 9.66667 12.9651 9.66667 13.3333C9.66667 13.7015 9.36821 14 9 14H7C6.6318 14 6.33333 13.7015 6.33333 13.3333Z"
      fill="currentColor"
    />
  </svg>
)

export const SprayerIcon = ({ size = 'w-5 h-4', color = 'text-black' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="16"
    viewBox="0 0 20 16"
    fill="none"
    className={`${size} ${color}`}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3.2 5.2C3.2 5.2 1.6 5.6 0 6.4V9.6L0.923145 9.96925C1.79629 8.77542 3.20762 8 4.8 8C6.88994 8 8.66787 9.33569 9.32686 11.2H11.4731C12.1321 9.33569 13.9101 8 16 8C16.2726 8 16.5398 8.02272 16.8 8.06637V5.9464L18.4 6.87015V7.2C18.4 7.42091 18.5791 7.6 18.8 7.6C19.0209 7.6 19.2 7.42091 19.2 7.2V1.6C19.2 1.37909 19.0209 1.2 18.8 1.2C18.5791 1.2 18.4 1.37909 18.4 1.6V1.9464L16.8 1.02265V0.4C16.8 0.179086 16.6209 0 16.4 0C16.1791 0 16 0.179086 16 0.4V5.6H9.44678C9.06953 5.6 8.74356 5.33648 8.66445 4.96762L7.6 0H2.4V0.8C2.4 1.24183 2.7582 1.6 3.2 1.6V5.2ZM18.4 5.9464L16.8 5.02265V1.94641L18.4 2.87015V5.9464ZM4.2 6V1.6H6.8L7.6 5.22892C6.23838 5.22892 5.33272 5.8373 4.82598 6.33498C4.68428 6.47418 4.2 6.39062 4.2 6ZM9.84736 0C9.31992 0 8.93682 0.501581 9.07559 1.01049L9.83926 3.8105C9.93418 4.15854 10.2503 4.4 10.611 4.4H14C14.4418 4.4 14.8 4.04183 14.8 3.6V0.8C14.8 0.358173 14.4418 0 14 0H9.84736ZM4.8 16C6.56729 16 8 14.5673 8 12.8C8 11.0327 6.56729 9.6 4.8 9.6C3.03271 9.6 1.6 11.0327 1.6 12.8C1.6 14.5673 3.03271 16 4.8 16ZM4.8 14.4C5.68369 14.4 6.4 13.6837 6.4 12.8C6.4 11.9163 5.68369 11.2 4.8 11.2C3.91631 11.2 3.2 11.9163 3.2 12.8C3.2 13.6837 3.91631 14.4 4.8 14.4ZM16 16C17.7673 16 19.2 14.5673 19.2 12.8C19.2 11.0327 17.7673 9.6 16 9.6C14.2327 9.6 12.8 11.0327 12.8 12.8C12.8 14.5673 14.2327 16 16 16ZM16 14.4C16.8837 14.4 17.6 13.6837 17.6 12.8C17.6 11.9163 16.8837 11.2 16 11.2C15.1163 11.2 14.4 11.9163 14.4 12.8C14.4 13.6837 15.1163 14.4 16 14.4ZM18.8 8.8C19.0209 8.8 19.2 8.62091 19.2 8.4C19.2 8.17909 19.0209 8 18.8 8C18.5791 8 18.4 8.17909 18.4 8.4C18.4 8.62091 18.5791 8.8 18.8 8.8ZM18.8 10C19.0209 10 19.2 9.82091 19.2 9.6C19.2 9.37909 19.0209 9.2 18.8 9.2C18.5791 9.2 18.4 9.37909 18.4 9.6C18.4 9.82091 18.5791 10 18.8 10ZM18 7.6C18 7.82091 17.8209 8 17.6 8C17.3791 8 17.2 7.82091 17.2 7.6C17.2 7.37909 17.3791 7.2 17.6 7.2C17.8209 7.2 18 7.37909 18 7.6ZM17.6 9.2C17.8209 9.2 18 9.02091 18 8.8C18 8.57909 17.8209 8.4 17.6 8.4C17.3791 8.4 17.2 8.57909 17.2 8.8C17.2 9.02091 17.3791 9.2 17.6 9.2Z"
      fill="currentColor"
    />
  </svg>
)

export const PlanterIcon = ({ size = 'size-4', color = 'text-black' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    className={`${size} ${color}`}
  >
    <path
      d="M4.33333 4.66667L3.33333 3.66667V1.33333H2.66667V0H10V1.33333H9.33333V3.66667L8.33333 4.66667H4.33333Z"
      fill="currentColor"
    />
    <path
      d="M10.6667 0V3.66667L11.6667 4.66667H14.3333L15.3333 3.66667V1.33333H16V0H10.6667Z"
      fill="currentColor"
    />
    <path
      d="M10.3102 7.96123L15.3333 7.33333L16 6H0V10.6667H1.78242C2.01982 10.1669 2.34342 9.71608 2.73401 9.33333H1.33333V7.33333H3.33333V8.83646C4.08922 8.30921 5.0085 8 6 8C7.9012 8 9.53686 9.13691 10.2642 10.7679C10.8726 10.0408 11.7347 9.53314 12.7143 9.38095L12 8.66667H10.3541C10.1585 8.66667 10 8.50814 10 8.31258C10 8.13401 10.133 7.98338 10.3102 7.96123Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.33333 12.6667C9.33333 14.5076 7.84095 16 6 16C4.15905 16 2.66667 14.5076 2.66667 12.6667C2.66667 10.8257 4.15905 9.33333 6 9.33333C7.84095 9.33333 9.33333 10.8257 9.33333 12.6667ZM6.33333 10.3333C6.33333 10.5174 6.18409 10.6667 6 10.6667C5.81591 10.6667 5.66667 10.5174 5.66667 10.3333C5.66667 10.1492 5.81591 10 6 10C6.18409 10 6.33333 10.1492 6.33333 10.3333ZM6.66667 12.6667C6.66667 13.0349 6.36819 13.3333 6 13.3333C5.63181 13.3333 5.33333 13.0349 5.33333 12.6667C5.33333 12.2985 5.63181 12 6 12C6.36819 12 6.66667 12.2985 6.66667 12.6667ZM4.66667 11C4.66667 11.1841 4.51743 11.3333 4.33333 11.3333C4.14924 11.3333 4 11.1841 4 11C4 10.8159 4.14924 10.6667 4.33333 10.6667C4.51743 10.6667 4.66667 10.8159 4.66667 11ZM8 11C8 11.1841 7.85076 11.3333 7.66667 11.3333C7.48257 11.3333 7.33333 11.1841 7.33333 11C7.33333 10.8159 7.48257 10.6667 7.66667 10.6667C7.85076 10.6667 8 10.8159 8 11ZM4 12.6667C4 12.8508 3.85076 13 3.66667 13C3.48257 13 3.33333 12.8508 3.33333 12.6667C3.33333 12.4826 3.48257 12.3333 3.66667 12.3333C3.85076 12.3333 4 12.4826 4 12.6667ZM8.66667 12.6667C8.66667 12.8508 8.51743 13 8.33333 13C8.14924 13 8 12.8508 8 12.6667C8 12.4826 8.14924 12.3333 8.33333 12.3333C8.51743 12.3333 8.66667 12.4826 8.66667 12.6667ZM4.66667 14.3333C4.66667 14.5174 4.51743 14.6667 4.33333 14.6667C4.14924 14.6667 4 14.5174 4 14.3333C4 14.1492 4.14924 14 4.33333 14C4.51743 14 4.66667 14.1492 4.66667 14.3333ZM8 14.3333C8 14.5174 7.85076 14.6667 7.66667 14.6667C7.48257 14.6667 7.33333 14.5174 7.33333 14.3333C7.33333 14.1492 7.48257 14 7.66667 14C7.85076 14 8 14.1492 8 14.3333ZM6.33333 15C6.33333 15.1841 6.18409 15.3333 6 15.3333C5.81591 15.3333 5.66667 15.1841 5.66667 15C5.66667 14.8159 5.81591 14.6667 6 14.6667C6.18409 14.6667 6.33333 14.8159 6.33333 15Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.3333 16C11.8606 16 10.6667 14.8061 10.6667 13.3333C10.6667 11.8606 11.8606 10.6667 13.3333 10.6667C14.8061 10.6667 16 11.8606 16 13.3333C16 14.8061 14.8061 16 13.3333 16ZM13.3333 12C13.5174 12 13.6667 11.8508 13.6667 11.6667C13.6667 11.4826 13.5174 11.3333 13.3333 11.3333C13.1492 11.3333 13 11.4826 13 11.6667C13 11.8508 13.1492 12 13.3333 12ZM13.3333 14C13.7015 14 14 13.7015 14 13.3333C14 12.9651 13.7015 12.6667 13.3333 12.6667C12.9651 12.6667 12.6667 12.9651 12.6667 13.3333C12.6667 13.7015 12.9651 14 13.3333 14ZM12 12.8333C12.1841 12.8333 12.3333 12.6841 12.3333 12.5C12.3333 12.3159 12.1841 12.1667 12 12.1667C11.8159 12.1667 11.6667 12.3159 11.6667 12.5C11.6667 12.6841 11.8159 12.8333 12 12.8333ZM12 14.5C12.1841 14.5 12.3333 14.3508 12.3333 14.1667C12.3333 13.9826 12.1841 13.8333 12 13.8333C11.8159 13.8333 11.6667 13.9826 11.6667 14.1667C11.6667 14.3508 11.8159 14.5 12 14.5ZM13.3333 15.3333C13.5174 15.3333 13.6667 15.1841 13.6667 15C13.6667 14.8159 13.5174 14.6667 13.3333 14.6667C13.1492 14.6667 13 14.8159 13 15C13 15.1841 13.1492 15.3333 13.3333 15.3333ZM14.6667 14.5C14.8508 14.5 15 14.3508 15 14.1667C15 13.9826 14.8508 13.8333 14.6667 13.8333C14.4826 13.8333 14.3333 13.9826 14.3333 14.1667C14.3333 14.3508 14.4826 14.5 14.6667 14.5ZM14.6667 12.8333C14.8508 12.8333 15 12.6841 15 12.5C15 12.3159 14.8508 12.1667 14.6667 12.1667C14.4826 12.1667 14.3333 12.3159 14.3333 12.5C14.3333 12.6841 14.4826 12.8333 14.6667 12.8333Z"
      fill="currentColor"
    />
  </svg>
)

export const CombineIcon = ({ size = 'size-4', color = 'text-black' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    className={`${size} ${color}`}
  >
    <path
      d="M3 0.666667L4 2H9.33333L10.3333 0.666667L9.33333 0H4L3 0.666667Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.3333 14C15.3333 15.1046 14.4379 16 13.3333 16C12.2288 16 11.3333 15.1046 11.3333 14C11.3333 12.8954 12.2288 12 13.3333 12C14.4379 12 15.3333 12.8954 15.3333 14ZM14 14C14 14.3682 13.7015 14.6667 13.3333 14.6667C12.9651 14.6667 12.6667 14.3682 12.6667 14C12.6667 13.6318 12.9651 13.3333 13.3333 13.3333C13.7015 13.3333 14 13.6318 14 14Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0.333333 4.66667L0.0592092 4.13412C-0.12499 3.76621 0.142898 3.33333 0.554781 3.33333H15.3333C15.7079 3.33333 16 3.58073 16 4L15.6667 5.33333H14.6667V4.33333H10.6667V6H13.3333L14.3333 8H16V10L9.57168 11.5306C8.91244 10.227 7.56059 9.33333 6 9.33333C3.79086 9.33333 2 11.1242 2 13.3333L0 12V10.6667H2V8.33333L0.333333 4.66667ZM3.41202 8.33333H6V5C6 4.63181 5.70152 4.33333 5.33333 4.33333H1L2.81574 7.96481C2.92867 8.19067 3.15951 8.33333 3.41202 8.33333Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.66667 13.3333C8.66667 14.8061 7.47276 16 6 16C4.52724 16 3.33333 14.8061 3.33333 13.3333C3.33333 11.8606 4.52724 10.6667 6 10.6667C7.47276 10.6667 8.66667 11.8606 8.66667 13.3333ZM7.33333 13.3333C7.33333 14.0697 6.73638 14.6667 6 14.6667C5.26362 14.6667 4.66667 14.0697 4.66667 13.3333C4.66667 12.597 5.26362 12 6 12C6.73638 12 7.33333 12.597 7.33333 13.3333Z"
      fill="currentColor"
    />
  </svg>
)

export const SidedressIcon = ({ size = 'size-4', color = 'text-black' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    className={`${size} ${color}`}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 0H2.66667V5.31266L3.96923 4H5.33333V3.33333H10L9.83333 4H10.6667C11.0349 4 11.3333 4.29848 11.3333 4.66667C11.3333 5.03486 11.0349 5.33333 10.6667 5.33333H9.5L9.33333 6H5.33333V5.33333L3.25287 7.41379L4.15571 7.82007C4.26252 7.65118 4.37854 7.48869 4.50309 7.33333H9.33333C9.81418 7.33333 10.2524 7.07512 10.4881 6.66667H10.6667C11.0764 6.66667 11.4574 6.54343 11.7746 6.33199C12.3892 6.77342 12.906 7.34232 13.2865 8H15.6667C15.8508 8 16 8.14924 16 8.33333V11.6667C16 11.8508 15.8508 12 15.6667 12H13.832C13.2399 14.3003 11.1518 16 8.66667 16C5.72115 16 3.33333 13.6122 3.33333 10.6667C3.33333 10.5264 3.33875 10.3873 3.34939 10.2497L7.15976 11.9817C7.52638 12.4015 8.06555 12.6667 8.66667 12.6667C9.53748 12.6667 10.2783 12.1101 10.5529 11.3333H8.66667L2.25 8.41667L1.33333 9.33333L0 8L0.666667 7.32817V2.66667H0V0ZM10.5529 10C10.2783 9.22321 9.53748 8.66667 8.66667 8.66667C8.12806 8.66667 7.63918 8.87957 7.2796 9.22582L9 10H10.5529ZM1.33333 2C1.70152 2 2 1.70152 2 1.33333C2 0.965144 1.70152 0.666667 1.33333 0.666667C0.965144 0.666667 0.666667 0.965144 0.666667 1.33333C0.666667 1.70152 0.965144 2 1.33333 2Z"
      fill="currentColor"
    />
  </svg>
)

export const StripTillIcon = ({ size = 'size-4', color = 'text-black' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    className={`${size} ${color}`}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0.666667 1C0.666667 0.447716 1.11442 0 1.66667 0H7.66667C8.00179 0 8.2985 0.164917 8.47998 0.418035L12.9391 1.20495C13.3324 1.27435 13.5843 1.66261 13.4875 2.05C13.4004 2.39825 13.0604 2.62118 12.7064 2.56217L8.24186 1.81809C8.07926 1.9327 7.88086 2 7.66667 2H7.5555L6.66667 3.33333H7.33333V4H11C11.5522 4 12 4.44772 12 5C12 5.24398 11.9126 5.46755 11.7674 5.64113L15.1702 9.56311C15.4116 9.84122 15.3817 10.2623 15.1037 10.5036C14.8255 10.7449 14.4045 10.715 14.1631 10.4369L12.8208 8.8898C12.6396 8.60286 12.4159 8.34548 12.1585 8.12653L10.9606 6.74582L10.7666 7.42191C10.5205 7.36398 10.2638 7.33333 10 7.33333C9.79134 7.33333 9.58708 7.35251 9.389 7.38921L9.7876 6H6.2762L8.16178 7.88556C7.78776 8.13325 7.46663 8.45437 7.21891 8.82836L4.39062 6H4V8.01736L5.20833 8.52083L6.14583 9.45833L6.66667 10.7083V11.9583L6.14583 13.2083L5.20833 14.1458L3.95833 14.6667H2.70833L1.45833 14.1458L0.520833 13.2083L0 11.9583V10.7083L0.520833 9.45833L1.45833 8.52083L2.66667 8.01736V6L0 7.33333V1.33333H0.723633C0.686686 1.22907 0.666667 1.11688 0.666667 1ZM1.33333 5.33333L2.33333 4.66667H5.33333V3.33333L6.22217 2H2.66667V2.66667H2C1.63184 2.66667 1.33333 2.96514 1.33333 3.33333V5.33333ZM2 1C2 0.815905 2.14925 0.666667 2.33333 0.666667H3.66667C3.85075 0.666667 4 0.815905 4 1C4 1.18409 3.85075 1.33333 3.66667 1.33333H2.33333C2.14925 1.33333 2 1.18409 2 1ZM5.33333 1C5.33333 0.815905 5.48258 0.666667 5.66667 0.666667H7C7.18408 0.666667 7.33333 0.815905 7.33333 1C7.33333 1.18409 7.18408 1.33333 7 1.33333H5.66667C5.48258 1.33333 5.33333 1.18409 5.33333 1ZM2.08333 12.1667L1.875 13.2083C1.83887 13.3542 1.63753 13.3375 1.5625 13.2083L0.833333 11.9583C0.758301 11.8292 0.794434 11.7083 0.9375 11.75L1.97917 11.9583C2.06527 11.9833 2.10417 12.0792 2.08333 12.1667ZM0.9375 10.9167C0.794434 10.9569 0.758301 10.8375 0.833333 10.7083L1.5625 9.45833C1.63753 9.32917 1.83887 9.31389 1.875 9.45833L2.08333 10.5C2.10417 10.5875 2.06527 10.6833 1.97917 10.7083L0.9375 10.9167ZM2.60417 13.8333C2.4541 13.8333 2.3916 13.6236 2.5 13.5208L3.22917 12.8958C3.29443 12.8333 3.37223 12.8333 3.4375 12.8958L4.16667 13.5208C4.27507 13.6236 4.21257 13.8333 4.0625 13.8333H2.60417ZM4.16667 9.14583L3.4375 9.77083C3.37223 9.83333 3.29443 9.83333 3.22917 9.77083L2.5 9.14583C2.3916 9.04306 2.4541 8.83333 2.60417 8.83333H4.0625C4.21257 8.83333 4.27507 9.04306 4.16667 9.14583ZM5.83333 11.9583L5.10417 13.2083C5.02913 13.3375 4.8278 13.3542 4.79167 13.2083L4.58333 12.1667C4.5625 12.0792 4.6014 11.9833 4.6875 11.9583L5.72917 11.75C5.87223 11.7083 5.9069 11.8292 5.83333 11.9583ZM4.6875 10.7083C4.6014 10.6833 4.5625 10.5875 4.58333 10.5L4.79167 9.45833C4.8278 9.31389 5.02913 9.32917 5.10417 9.45833L5.83333 10.7083C5.9069 10.8375 5.87223 10.9569 5.72917 10.9167L4.6875 10.7083ZM4.16667 11.3333C4.16667 11.7936 3.79362 12.1667 3.33333 12.1667C2.87305 12.1667 2.5 11.7936 2.5 11.3333C2.5 10.8731 2.87305 10.5 3.33333 10.5C3.79362 10.5 4.16667 10.8731 4.16667 11.3333Z"
      fill="currentColor"
    />
    <path
      d="M10 14C9.12744 14 8.33317 13.6648 7.7391 13.1161L7.39111 14.16C7.14307 14.9042 6.57992 15.5013 5.85173 15.7927L5.33333 16H8.72705C9.29053 16 9.79313 15.6458 9.98275 15.1151L10.389 13.9775C10.2614 13.9924 10.1315 14 10 14Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.3836 8.09692C10.3644 8.32476 10.1735 8.50371 9.94076 8.50371C9.69515 8.50371 9.49626 8.30472 9.49626 8.05926V8.05334L9.49723 8.02888L9.46891 8C9.23421 8.04616 9.04264 8.14609 8.8374 8.25316L8.80501 8.27004C8.92529 8.46614 8.8571 8.73731 8.6722 8.87764C8.50407 9.0066 8.21468 8.96878 8.07471 8.81013C7.89323 9.00083 7.72949 9.17845 7.61003 9.41772C7.8807 9.53133 7.96257 9.7412 7.87549 10.0253C7.80729 10.2511 7.56657 10.3457 7.3444 10.2954C7.31445 10.5224 7.35091 10.7422 7.3877 10.963C7.39567 11.0104 7.40348 11.0579 7.41081 11.1055C7.632 11.0497 7.80111 11.1533 7.87549 11.3755C7.95068 11.5995 7.88607 11.8239 7.67627 11.9156C7.81022 12.1666 7.93669 12.3846 8.14111 12.5907C8.28792 12.4135 8.55013 12.3874 8.73861 12.5232C8.92887 12.6599 8.9873 12.8636 8.87142 13.0633C9.10417 13.1784 9.33789 13.292 9.60173 13.3333C9.62044 13.0613 9.87012 12.8705 10.1328 12.9283C10.3169 12.9696 10.4461 13.1372 10.4647 13.3333C10.7275 13.2857 10.9648 13.1838 11.195 13.0633C11.0747 12.8672 11.1419 12.596 11.3278 12.4557C11.5137 12.3145 11.7739 12.3496 11.9253 12.5232C12.1058 12.3316 12.2705 12.1549 12.39 11.9156C12.1794 11.8284 12.0563 11.5329 12.1245 11.308C12.1935 11.0831 12.4334 10.9876 12.6556 11.038C12.6868 10.8025 12.6458 10.5619 12.6061 10.3287L12.5892 10.2278C12.3688 10.2836 12.1997 10.1809 12.1245 9.95781C12.0493 9.73705 12.063 9.53389 12.3236 9.41772C12.1888 9.16485 12.0633 8.95034 11.8589 8.74261C11.7129 8.91983 11.4508 8.94596 11.2614 8.81013C11.1374 8.72131 11.0472 8.40971 11.1286 8.27004L11.0594 8.23634C10.8429 8.1305 10.6598 8.04098 10.3983 8C10.3968 8.02052 10.3944 8.04059 10.3911 8.06013L10.3875 8.07954L10.3836 8.09692ZM10.6667 10.6667C10.6667 11.0349 10.3682 11.3333 10 11.3333C9.63184 11.3333 9.33333 11.0349 9.33333 10.6667C9.33333 10.2985 9.63184 10 10 10C10.3682 10 10.6667 10.2985 10.6667 10.6667Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16 13C16 13.9205 15.2537 14.6667 14.3333 14.6667C13.4129 14.6667 12.6667 13.9205 12.6667 13C12.6667 12.0795 13.4129 11.3333 14.3333 11.3333C15.2537 11.3333 16 12.0795 16 13ZM15 13C15 13.3682 14.7015 13.6667 14.3333 13.6667C13.9652 13.6667 13.6667 13.3682 13.6667 13C13.6667 12.6318 13.9652 12.3333 14.3333 12.3333C14.7015 12.3333 15 12.6318 15 13Z"
      fill="currentColor"
    />
  </svg>
)

export const FreehandIcon = ({ className }: { className: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="25"
    height="24"
    viewBox="0 0 25 24"
    fill="none"
    className={className}
  >
    <path
      d="M5.91275 22.2516C6.04272 21.7155 6.53729 21.3785 7.04706 21.4584L7.14936 21.4792L7.48111 21.5617C8.27033 21.7434 9.17199 21.8508 10.1957 21.8623C10.7574 21.8686 11.2079 22.3523 11.2019 22.9426C11.1958 23.5328 10.7355 24.0062 10.1738 23.9999C9.02945 23.9871 7.98731 23.8673 7.04507 23.6503L6.64776 23.5511L6.54744 23.5188C6.06356 23.3325 5.78281 22.7877 5.91275 22.2516ZM14.9167 21.3582C15.4681 21.2458 16.0016 21.6252 16.1086 22.2047C16.2153 22.784 15.8553 23.3448 15.304 23.4572C14.7219 23.5759 14.1586 23.6747 13.6135 23.7536C13.0571 23.8342 12.543 23.4251 12.4663 22.8403C12.3896 22.2556 12.7789 21.7164 13.3354 21.6358C13.842 21.5624 14.3689 21.4699 14.9167 21.3582ZM1.56524 17.3553C2.05475 17.1515 2.60518 17.3746 2.84258 17.8553L2.88529 17.9555L3.01739 18.2738C3.34819 19.0065 3.84616 19.6682 4.53808 20.2194C4.98571 20.576 5.07311 21.2468 4.73375 21.7172C4.3944 22.1875 3.757 22.2793 3.30941 21.9228C2.28687 21.1082 1.55001 20.0972 1.08351 18.97L0.994117 18.7425L0.961339 18.6392C0.820716 18.1179 1.07558 17.5592 1.56524 17.3553ZM12.7315 16.5819C13.2883 16.5041 13.7997 16.9153 13.8737 17.5004C13.9478 18.0855 13.5565 18.6229 12.9997 18.7007C12.4872 18.7724 11.9535 18.8093 11.4005 18.8093C10.839 18.809 10.3834 18.3306 10.3834 17.7405C10.3837 17.1506 10.8392 16.6719 11.4005 16.6717C11.8663 16.6717 12.3104 16.6408 12.7315 16.5819ZM17.2628 14.2522C17.6445 13.8194 18.288 13.7937 18.7 14.1948C19.112 14.596 19.1364 15.2721 18.7546 15.7051C18.0396 16.5162 17.149 17.2173 16.0996 17.7467C15.5933 18.0021 14.986 17.7778 14.7429 17.2457C14.4997 16.7136 14.7132 16.0754 15.2196 15.8199C16.0412 15.4054 16.7235 14.8639 17.2628 14.2522ZM1.53048 11.0583C1.79474 10.5931 2.35652 10.4045 2.83364 10.6387C3.3425 10.8887 3.56221 11.5256 3.32431 12.0603L3.16837 12.4308C2.82175 13.2987 2.60564 14.1814 2.5297 15.0361L2.5148 15.1446C2.41516 15.676 1.94647 16.052 1.42221 16.0005C0.86318 15.9455 0.452449 15.4252 0.504438 14.8378L0.556088 14.3702C0.702234 13.2809 1.01976 12.1929 1.48181 11.1543L1.53048 11.0583ZM20.4611 12.6876C20.2866 13.2485 19.7126 13.5544 19.1788 13.3713C18.6449 13.188 18.3529 12.5848 18.5272 12.0238L20.4611 12.6876ZM6.29913 5.52842C6.76265 5.2916 7.33124 5.45764 7.60825 5.92818C7.90351 6.43013 7.75509 7.08836 7.27749 7.39885L6.95766 7.61699C6.22011 8.13761 5.55676 8.75161 4.98107 9.43001L4.90757 9.5083C4.52734 9.87379 3.93774 9.88484 3.54481 9.51665C3.12564 9.12377 3.08954 8.4479 3.46337 8.00736L3.76333 7.66605C4.47841 6.88047 5.29752 6.1734 6.20775 5.58165L6.29913 5.52842ZM12.9798 3.69974C13.4924 3.75691 13.891 4.21418 13.8886 4.76751C13.886 5.35761 13.4282 5.8337 12.8666 5.83111L12.5398 5.83424C12.3184 5.8393 12.0904 5.85096 11.8554 5.86869L11.4979 5.9C10.9897 5.95114 10.497 6.04203 10.0219 6.16929L9.92056 6.19121C9.4106 6.27149 8.91537 5.93431 8.78526 5.39795C8.64653 4.82596 8.97596 4.24426 9.52028 4.09846L9.95533 3.99095C10.3942 3.89199 10.8442 3.81805 11.3042 3.77176L11.7094 3.73627C12.111 3.70597 12.4996 3.69175 12.8755 3.69348L12.9798 3.69974Z"
      fill="currentColor"
    />
    <path
      d="M18.5272 12.0238L20.4611 12.6876C20.7795 11.6626 20.8847 10.5881 20.7481 9.54587L20.7114 9.32407L18.6682 11.473C18.6312 11.6565 18.5842 11.8403 18.5272 12.0238Z"
      fill="currentColor"
    />
    <path
      d="M18.9452 3.18567L11.3407 10.7902C11.2787 10.8522 11.2364 10.9312 11.2192 11.0172L10.6795 13.716C10.667 13.7782 10.7218 13.833 10.784 13.8205L13.4828 13.2808C13.5688 13.2636 13.6478 13.2213 13.7098 13.1593L21.3143 5.55478L18.9452 3.18567Z"
      fill="currentColor"
    />
    <path
      d="M24.2398 1.37291L23.1271 0.260211C22.7801 -0.0867372 22.2176 -0.0867368 21.8707 0.260211L20.1298 2.00112L22.4989 4.37023L24.2398 2.62932C24.5867 2.28237 24.5867 1.71986 24.2398 1.37291Z"
      fill="currentColor"
    />
  </svg>
)

export const CircleDrawIcon = ({ className }: { className: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    className={className}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8 0C8.53338 0.000127493 8.99277 0.313986 9.20605 0.766602C10.7008 1.01583 12.0849 1.72281 13.1641 2.79297C14.2576 3.87762 14.9797 5.27825 15.2324 6.79395C15.6856 7.00689 15.9999 7.46634 16 8C15.9999 8.53395 15.685 8.99307 15.2314 9.20605C14.9897 10.6554 14.3198 12.003 13.2998 13.0693C12.2046 14.2143 10.7674 14.971 9.20605 15.2314C8.99307 15.685 8.53395 15.9999 8 16C7.46634 15.9999 7.00689 15.6856 6.79395 15.2324L6.73145 15.2227C5.24702 14.9619 3.87544 14.2492 2.80762 13.1787C1.72921 12.0973 1.0169 10.7074 0.766602 9.20508C0.314189 8.99172 0.000127458 8.53323 0 8C0.000127564 7.46612 0.314287 7.0058 0.767578 6.79297C1.0193 5.28434 1.73653 3.89029 2.82227 2.80762C3.90322 1.72988 5.29232 1.01801 6.79395 0.767578C7.00686 0.314489 7.46627 0.000127529 8 0ZM8 14C7.63213 14.0001 7.33431 14.2982 7.33398 14.666C7.33398 15.0341 7.63192 15.3329 8 15.333C8.36804 15.3328 8.66699 15.0341 8.66699 14.666C8.66666 14.2982 8.36784 14.0002 8 14ZM9.08984 2.09863C8.84844 2.44162 8.45098 2.66688 8 2.66699C7.54879 2.66688 7.15036 2.4419 6.90918 2.09863C5.71996 2.31846 4.62255 2.89271 3.7627 3.75L3.5918 3.92773C2.82215 4.76084 2.30377 5.79449 2.09766 6.90918C2.4413 7.15031 2.66688 7.54851 2.66699 8C2.66688 8.45098 2.44162 8.84844 2.09863 9.08984C2.31843 10.2798 2.89301 11.379 3.75098 12.2393C4.61208 13.1023 5.71399 13.6804 6.9082 13.9014C7.14934 13.558 7.54856 13.3331 8 13.333C8.45093 13.3331 8.84941 13.5577 9.09082 13.9004C10.3286 13.6716 11.4657 13.0612 12.3379 12.1494C13.1483 11.3022 13.6884 10.2376 13.9004 9.09082C13.5577 8.84941 13.3331 8.45093 13.333 8C13.3331 7.54905 13.5576 7.1504 13.9004 6.90918C13.6784 5.70866 13.0965 4.60116 12.2266 3.73828C11.3681 2.88702 10.2737 2.31729 9.08984 2.09863ZM1.33301 7.33398C0.96509 7.33416 0.66719 7.63209 0.666992 8C0.667168 8.36793 0.965076 8.66682 1.33301 8.66699C1.70109 8.66699 1.99982 8.36804 2 8C1.9998 7.63198 1.70108 7.33398 1.33301 7.33398ZM14.666 7.33398C14.2983 7.33438 14.0002 7.63222 14 8C14.0002 8.3678 14.2983 8.6666 14.666 8.66699C15.0341 8.66699 15.3328 8.36804 15.333 8C15.3328 7.63198 15.0341 7.33398 14.666 7.33398ZM8 0.666992C7.63203 0.667124 7.33416 0.965049 7.33398 1.33301C7.33398 1.70112 7.63192 1.99987 8 2C8.36804 1.99982 8.66699 1.70109 8.66699 1.33301C8.66682 0.965076 8.36793 0.667168 8 0.666992Z"
      fill="currentColor"
    />
  </svg>
)

export const PolygonIcon = ({ className }: { className: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="17"
    height="16"
    viewBox="0 0 17 16"
    fill="none"
    className={className}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.166 0C15.9023 0 16.4998 0.596778 16.5 1.33301C16.5 1.8262 16.2313 2.25548 15.833 2.48633V13.5127C16.2314 13.7432 16.4998 14.1729 16.5 14.666C16.5 15.4024 15.9024 16 15.166 16C14.6729 15.9998 14.2432 15.7314 14.0127 15.333H2.9873C2.75652 15.7315 2.32638 16 1.83301 16C1.09678 15.9998 0.5 15.4023 0.5 14.666C0.500236 14.1728 0.768734 13.7432 1.16699 13.5127V8.4873C0.768598 8.25654 0.5 7.82643 0.5 7.33301C0.500176 6.59689 1.09689 6.00018 1.83301 6C2.56928 6 3.16682 6.59678 3.16699 7.33301C3.16699 7.8261 2.89815 8.25545 2.5 8.48633V13.5127C2.70211 13.6297 2.87023 13.798 2.9873 14H14.0137C14.1307 13.7981 14.2981 13.6296 14.5 13.5127V2.48633C14.2982 2.36926 14.1296 2.20197 14.0127 2H6.65332C6.42249 2.39835 5.99226 2.66699 5.49902 2.66699C4.76287 2.66673 4.16602 2.06922 4.16602 1.33301C4.16619 0.596941 4.76298 0.000263843 5.49902 0C5.99241 0 6.42254 0.268637 6.65332 0.666992H14.0127C14.2432 0.268734 14.6728 0.000235555 15.166 0ZM1.83301 14C1.46517 14.0002 1.16732 14.2982 1.16699 14.666C1.16699 15.0341 1.46497 15.3328 1.83301 15.333C2.2012 15.333 2.5 15.0342 2.5 14.666C2.49967 14.2981 2.20099 14 1.83301 14ZM15.166 14C14.7984 14.0004 14.5003 14.2984 14.5 14.666C14.5 15.034 14.7982 15.3326 15.166 15.333C15.5342 15.333 15.833 15.0342 15.833 14.666C15.8327 14.2981 15.534 14 15.166 14ZM1.83301 6.66699C1.46508 6.66717 1.16717 6.96508 1.16699 7.33301C1.16699 7.70109 1.46497 7.99982 1.83301 8C2.2012 8 2.5 7.7012 2.5 7.33301C2.49982 6.96497 2.20109 6.66699 1.83301 6.66699ZM5.49902 0.666992C5.13113 0.667212 4.83318 0.965103 4.83301 1.33301C4.83301 1.70106 5.13102 1.99978 5.49902 2C5.86721 2 6.16602 1.7012 6.16602 1.33301C6.16584 0.964968 5.8671 0.666992 5.49902 0.666992ZM15.166 0.666992C14.7983 0.667387 14.5002 0.965212 14.5 1.33301C14.5 1.70095 14.7982 1.9996 15.166 2C15.5342 2 15.833 1.7012 15.833 1.33301C15.8328 0.964968 15.5341 0.666992 15.166 0.666992Z"
      fill="currentColor"
    />
  </svg>
)
export const RectangleIcon = ({ className }: { className: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="12"
    viewBox="0 0 16 12"
    fill="none"
    className={className}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.666 0.666016C15.4023 0.666016 15.9998 1.26281 16 1.99902C16 2.49221 15.7313 2.9215 15.333 3.15234V8.8457C15.7314 9.07625 15.9999 9.50583 16 9.99902C16 10.7354 15.4024 11.333 14.666 11.333C14.173 11.3328 13.7432 11.0643 13.5127 10.666H2.4873C2.25649 11.0644 1.82629 11.333 1.33301 11.333C0.596778 11.3328 0 10.7353 0 9.99902C0.000132748 9.50561 0.268492 9.07519 0.666992 8.84473V3.15332C0.268598 2.92255 0 2.49244 0 1.99902C0.000175889 1.2629 0.596886 0.666192 1.33301 0.666016C1.82642 0.666016 2.25654 0.934613 2.4873 1.33301H13.5127C13.7432 0.934767 14.1729 0.666251 14.666 0.666016ZM1.33301 9.33301C0.965103 9.33318 0.667212 9.63113 0.666992 9.99902C0.666992 10.3671 0.964968 10.6658 1.33301 10.666C1.7012 10.666 2 10.3672 2 9.99902C1.99978 9.63102 1.70106 9.33301 1.33301 9.33301ZM14.666 9.33301C14.2983 9.3334 14.0002 9.63126 14 9.99902C14 10.367 14.2982 10.6656 14.666 10.666C15.0342 10.666 15.333 10.3672 15.333 9.99902C15.3328 9.63102 15.0341 9.33301 14.666 9.33301ZM2.4873 2.66602C2.37033 2.86791 2.20185 3.0353 2 3.15234V8.8457C2.20212 8.96273 2.37025 9.13094 2.4873 9.33301H13.5127C13.6297 9.13087 13.7979 8.96274 14 8.8457V3.15234C13.7982 3.03527 13.6296 2.86799 13.5127 2.66602H2.4873ZM1.33301 1.33301C0.96509 1.33318 0.66719 1.63111 0.666992 1.99902C0.666992 2.3671 0.964968 2.66584 1.33301 2.66602C1.7012 2.66602 2 2.36721 2 1.99902C1.9998 1.631 1.70108 1.33301 1.33301 1.33301ZM14.666 1.33301C14.2983 1.3334 14.0002 1.63126 14 1.99902C14 2.36697 14.2982 2.66562 14.666 2.66602C15.0342 2.66602 15.333 2.36721 15.333 1.99902C15.3328 1.63102 15.0341 1.33301 14.666 1.33301Z"
      fill="currentColor"
    />
  </svg>
)
export const FieldBoundaryIcon = ({ className }: { className: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    className={className}
  >
    (
    <path
      d="M22 0.5C23.1046 0.5 24 1.39543 24 2.5C24 3.24028 23.5978 3.88661 23 4.23242V20.7676C23.5978 21.1134 24 21.7597 24 22.5C24 23.6046 23.1046 24.5 22 24.5C21.2597 24.5 20.6134 24.0978 20.2676 23.5H3.73242C3.38661 24.0978 2.74028 24.5 2 24.5C0.895431 24.5 0 23.6046 0 22.5C0 21.7597 0.402202 21.1134 1 20.7676V10.2324C0.402202 9.88661 0 9.24028 0 8.5C0 7.39543 0.895431 6.5 2 6.5C2.27051 6.5 2.52833 6.55404 2.76367 6.65137L8.00488 2.64355C8.00154 2.59626 8 2.54815 8 2.5C8 1.39543 8.89543 0.5 10 0.5C10.7403 0.5 11.3866 0.902202 11.7324 1.5H20.2676C20.6134 0.902202 21.2597 0.5 22 0.5ZM2 21.5C1.44772 21.5 1 21.9477 1 22.5C1 23.0523 1.44772 23.5 2 23.5C2.55228 23.5 3 23.0523 3 22.5C3 21.9477 2.55228 21.5 2 21.5ZM22 21.5C21.4477 21.5 21 21.9477 21 22.5C21 23.0523 21.4477 23.5 22 23.5C22.5523 23.5 23 23.0523 23 22.5C23 21.9477 22.5523 21.5 22 21.5ZM11.7324 3.5C11.3866 4.0978 10.7403 4.5 10 4.5C9.68806 4.5 9.39301 4.42815 9.12988 4.30078L3.98242 8.23633C3.99375 8.32247 4 8.41077 4 8.5C4 9.24028 3.5978 9.88661 3 10.2324V20.7676C3.30362 20.9432 3.55678 21.1964 3.73242 21.5H20.2676C20.4432 21.1964 20.6964 20.9432 21 20.7676V4.23242C20.6964 4.05678 20.4432 3.80362 20.2676 3.5H11.7324ZM6 9C6.55228 9 7 9.44772 7 10V19C7 19.5523 6.55228 20 6 20C5.44772 20 5 19.5523 5 19V10C5 9.44771 5.44772 9 6 9ZM10 6.5C10.5523 6.5 11 6.94772 11 7.5V19C11 19.5523 10.5523 20 10 20C9.44771 20 9 19.5523 9 19V7.5C9 6.94772 9.44771 6.5 10 6.5ZM14 5C14.5523 5 15 5.44772 15 6V19C15 19.5523 14.5523 20 14 20C13.4477 20 13 19.5523 13 19V6C13 5.44772 13.4477 5 14 5ZM18 5C18.5523 5 19 5.44772 19 6V19C19 19.5523 18.5523 20 18 20C17.4477 20 17 19.5523 17 19V6C17 5.44772 17.4477 5 18 5ZM2 7.5C1.44772 7.5 1 7.94772 1 8.5C1 9.05228 1.44772 9.5 2 9.5C2.55228 9.5 3 9.05228 3 8.5C3 7.94772 2.55228 7.5 2 7.5ZM10 1.5C9.44771 1.5 9 1.94772 9 2.5C9 3.05228 9.44771 3.5 10 3.5C10.5523 3.5 11 3.05228 11 2.5C11 1.94772 10.5523 1.5 10 1.5ZM22 1.5C21.4477 1.5 21 1.94772 21 2.5C21 3.05228 21.4477 3.5 22 3.5C22.5523 3.5 23 3.05228 23 2.5C23 1.94772 22.5523 1.5 22 1.5Z"
      fill="currentColor"
    />
    )
  </svg>
)
export const InnerAreaIcon = ({ className }: { className: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    className={className}
  >
    <path
      d="M2 0.5C1.44771 0.5 1 0.947716 1 1.5V9.5C1 10.0523 1.44771 10.5 2 10.5C2.55229 10.5 3 10.0523 3 9.5V1.5C3 0.947716 2.55229 0.5 2 0.5Z"
      fill="currentColor"
    />
    <path
      d="M23 23.5C23 24.0523 22.5523 24.5 22 24.5C21.4477 24.5 21 24.0523 21 23.5V15.5C21 14.9477 21.4477 14.5 22 14.5C22.5523 14.5 23 14.9477 23 15.5V23.5Z"
      fill="currentColor"
    />
    <path
      d="M18 24.5C18.5523 24.5 19 24.0523 19 23.5V20.5C19 19.9477 18.5523 19.5 18 19.5C17.4477 19.5 17 19.9477 17 20.5V23.5C17 24.0523 17.4477 24.5 18 24.5Z"
      fill="currentColor"
    />
    <path
      d="M14 24.5C14.5523 24.5 15 24.0523 15 23.5V20C15 19.4477 14.5523 19 14 19C13.4477 19 13 19.4477 13 20V23.5C13 24.0523 13.4477 24.5 14 24.5Z"
      fill="currentColor"
    />
    <path
      d="M10 24.5C10.5523 24.5 11 24.0523 11 23.5V20C11 19.4477 10.5523 19 10 19C9.44771 19 9 19.4477 9 20V23.5C9 24.0523 9.44771 24.5 10 24.5Z"
      fill="currentColor"
    />
    <path
      d="M6 24.5C6.55229 24.5 7 24.0523 7 23.5V20C7 19.4477 6.55229 19 6 19C5.44771 19 5 19.4477 5 20V23.5C5 24.0523 5.44771 24.5 6 24.5Z"
      fill="currentColor"
    />
    <path
      d="M2 24.5C2.55229 24.5 3 24.0523 3 23.5V20.5C3 19.9477 2.55229 19.5 2 19.5C1.44771 19.5 1 19.9477 1 20.5V23.5C1 24.0523 1.44771 24.5 2 24.5Z"
      fill="currentColor"
    />
    <path
      d="M5 1.5C5 0.947716 5.44771 0.5 6 0.5C6.55229 0.5 7 0.947716 7 1.5V4.5C7 5.05228 6.55229 5.5 6 5.5C5.44771 5.5 5 5.05228 5 4.5V1.5Z"
      fill="currentColor"
    />
    <path
      d="M9 1.5C9 0.947716 9.44771 0.5 10 0.5C10.5523 0.5 11 0.947716 11 1.5V5C11 5.55228 10.5523 6 10 6C9.44771 6 9 5.55228 9 5V1.5Z"
      fill="currentColor"
    />
    <path
      d="M13 1.5C13 0.947716 13.4477 0.5 14 0.5C14.5523 0.5 15 0.947716 15 1.5V5C15 5.55228 14.5523 6 14 6C13.4477 6 13 5.55228 13 5V1.5Z"
      fill="currentColor"
    />
    <path
      d="M17 1.5C17 0.947716 17.4477 0.5 18 0.5C18.5523 0.5 19 0.947716 19 1.5V5C19 5.55228 18.5523 6 18 6C17.4477 6 17 5.55228 17 5V1.5Z"
      fill="currentColor"
    />
    <path
      d="M21 1.5C21 0.947716 21.4477 0.5 22 0.5C22.5523 0.5 23 0.947716 23 1.5V4.5C23 5.05228 22.5523 5.5 22 5.5C21.4477 5.5 21 5.05228 21 4.5V1.5Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M22 10.5C23.1046 10.5 24 9.60457 24 8.5C24 7.39543 23.1046 6.5 22 6.5C21.2597 6.5 20.6134 6.9022 20.2676 7.5H8.73244C8.38663 6.9022 7.74028 6.5 7 6.5C5.89543 6.5 5 7.39543 5 8.5C5 8.83945 5.08456 9.15915 5.23378 9.43917L2.07001 14.5012L2.04102 14.5004C2.02737 14.5001 2.0137 14.5 2 14.5C0.895432 14.5 0 15.3954 0 16.5C0 17.6046 0.895432 18.5 2 18.5C2.74028 18.5 3.38663 18.0978 3.73244 17.5H15.2676C15.6134 18.0978 16.2597 18.5 17 18.5C18.1046 18.5 19 17.6046 19 16.5C19 16.1606 18.9154 15.8409 18.7662 15.5608L21.93 10.4988C21.9532 10.4996 21.9766 10.5 22 10.5ZM6.92999 10.4988L3.80424 15.5H15.2676C15.6134 14.9022 16.2597 14.5 17 14.5C17.0074 14.5 17.0148 14.5 17.0221 14.5001C17.0297 14.5002 17.0372 14.5003 17.0447 14.5005L17.07 14.5012L20.1958 9.5H8.73244C8.38663 10.0978 7.74028 10.5 7 10.5C6.97656 10.5 6.95323 10.4996 6.92999 10.4988ZM7 7.5C7.55229 7.5 8 7.94772 8 8.5C8 9.05228 7.55229 9.5 7 9.5C6.44771 9.5 6 9.05228 6 8.5C6 7.94772 6.44771 7.5 7 7.5ZM2 15.5C2.55229 15.5 3 15.9477 3 16.5C3 17.0523 2.55229 17.5 2 17.5C1.44771 17.5 1 17.0523 1 16.5C1 15.9477 1.44771 15.5 2 15.5ZM17 15.5C17.5523 15.5 18 15.9477 18 16.5C18 17.0523 17.5523 17.5 17 17.5C16.4477 17.5 16 17.0523 16 16.5C16 15.9477 16.4477 15.5 17 15.5ZM22 7.5C22.5523 7.5 23 7.94772 23 8.5C23 9.05228 22.5523 9.5 22 9.5C21.4477 9.5 21 9.05228 21 8.5C21 7.94772 21.4477 7.5 22 7.5Z"
      fill="currentColor"
    />
  </svg>
)

export const EulaIcon = ({ className }: { className: string }) => (
  <svg
    width="21"
    height="24"
    viewBox="0 0 21 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M18.2861 18.4092C18.2861 18.2085 18.1235 18.0461 17.9229 18.0459H14.2783C14.0776 18.046 13.915 18.2084 13.915 18.4092V21.5908C13.915 21.7916 14.0776 21.955 14.2783 21.9551H17.9229C18.1235 21.9549 18.2861 21.7915 18.2861 21.5908V18.4092ZM9.91113 12C10.4634 12 10.9111 12.4477 10.9111 13C10.9111 13.5523 10.4634 14 9.91113 14H4.91113C4.35893 13.9999 3.91113 13.5522 3.91113 13C3.91113 12.4478 4.35893 12.0001 4.91113 12H9.91113ZM17.5361 9.5V4.13281C17.5361 2.95496 16.5812 2 15.4033 2H4.41895C3.2411 2 2.28613 2.95496 2.28613 4.13281V19.8672C2.28613 21.045 3.2411 22 4.41895 22H9.28613C9.83842 22 10.2861 22.4477 10.2861 23C10.2861 23.5523 9.83842 24 9.28613 24H4.41895C2.13653 24 0.286133 22.1496 0.286133 19.8672V4.13281C0.286133 1.8504 2.13653 0 4.41895 0H15.4033C17.6857 0 19.5361 1.8504 19.5361 4.13281V9.5C19.5361 10.0523 19.0884 10.5 18.5361 10.5C17.984 10.4998 17.5361 10.0522 17.5361 9.5ZM12.6475 8.1123C13.1996 8.11232 13.6472 8.56025 13.6475 9.1123C13.6475 9.66458 13.1997 10.1123 12.6475 10.1123H4.91113C4.35893 10.1122 3.91113 9.66453 3.91113 9.1123C3.91139 8.5603 4.35909 8.1124 4.91113 8.1123H12.6475ZM14.9111 4C15.4634 4 15.9111 4.44772 15.9111 5C15.9111 5.55228 15.4634 6 14.9111 6H4.91113C4.35893 5.9999 3.91113 5.55223 3.91113 5C3.91113 4.44777 4.35893 4.0001 4.91113 4H14.9111ZM17.376 15.2227C17.376 14.5331 16.8007 13.9551 16.1006 13.9551C15.4004 13.9551 14.8252 14.5331 14.8252 15.2227V16.0459H17.376V15.2227ZM19.376 16.5469C19.9295 16.9795 20.2861 17.6522 20.2861 18.4092V21.5908C20.2861 22.8961 19.2281 23.9549 17.9229 23.9551H14.2783C12.973 23.955 11.915 22.8961 11.915 21.5908V18.4092C11.915 17.6522 12.2717 16.9795 12.8252 16.5469V15.2227C12.8252 13.4221 14.3022 11.9551 16.1006 11.9551C17.8989 11.9551 19.376 13.4222 19.376 15.2227V16.5469Z"
      fill="currentColor"
    />
  </svg>
)
